import React, { useState, useEffect, useRef, startTransition } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import ScheduleView from '../components/ScheduleView'
import { SaveIcon, ChevronDownIcon, TrashIcon, PlusIcon, ArrowDownIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { BillingDayOfMonthSelect } from '../components/BillingDayOfMonthSelect'
import {
  fetchPackageChildItems,
  buildPackageChildServices,
} from '../lib/serviceItemPackages'
import { logActivity, getEntityActivities, ActivityLog } from '../utils/activityLogger'
import { fetchServiceItemsGrouped, ServiceItemGrouped } from '../utils/serviceItems'
import { ServiceItemSelect } from '../components/service/ServiceItemSelect'
import {
  VisitGroupEditor,
  validateVisitGroups,
  ContractVisitGroup,
  describeVisitGroupCadence,
} from '../components/VisitGroupEditor'
import { VisitScheduleBoard } from '../components/VisitScheduleBoard'
import {
  describeContractServiceSchedule,
  fillEmptyVisitGroupDaysFromServices,
  getGroupAssignments,
  normalizeVisitGroupFromDb,
  syncGroupServiceIds,
  VisitGroupServiceAssignment,
} from '../lib/visitGroupBoardOps'
import { scrollAndFlash, ensureScrollFlashCSS } from '../utils/scrollAndFlash'
import { useCombinationRules } from '../hooks/useCombinationRules'
import { useVisitGroupTemplates } from '../hooks/useVisitGroupTemplates'
import ConsistencyWarnings from '../components/agreement/ConsistencyWarnings'
import {
  checkConsistency,
  splitIssues,
  getDefaultTemplateTypeForServiceType,
} from '../utils/consistencyRules'
import { SERVICE_TYPE_OPTIONS, isDsnyServiceType } from '../lib/serviceTypeOptions'
import { getDSNYPickupDays, pickupSchedulesToPickupDayNames } from '../utils/dsnyHelpers'
import {
  DsnyRowRecurrenceType,
  DsnyMonthlyMode,
  RECURRENCE_TYPE_OPTIONS,
  OCCURRENCE_OPTIONS,
  WEEKDAY_OPTIONS,
  getRowRecurrenceType,
  isSnapToClosestRow,
  buildRowRecurrenceFields,
} from '../lib/dsnyRowRecurrence'
import { AgreementState, IssueTarget, DocumentData, PricingMode, ContractType } from '../types/agreement'
import { buildDefaultDocumentData } from '../lib/proposalDocumentTemplates'
import { normalizeUnitType } from '../lib/unitTypeHelpers'
import { buildRateCardLineItems, hasRateCardSeed } from '../lib/rateCardLineItems'
import { computeEndDateFromMonths, inferDurationMonths } from '../lib/contractDuration'
import {
  CONTRACT_STATUS_OPTIONS,
  contractWorkflowType,
  getAllowedContractTransitions,
  formatContractStatusLabel,
} from '../lib/contractStatusWorkflow'
import { ContractAssignmentFields } from '../components/contract/ContractAssignmentFields'
import { ContractPropertyScopeFields } from '../components/contract/ContractPropertyScopeFields'
import {
  fetchContractProperties,
  syncContractProperties,
  type ContractScopeType,
} from '../api/turnoverContracts'
import {
  reapplyAssignmentDefaultsForContract,
  type AssignmentSource,
} from '../api/jobAssignmentDefaults'
import DocumentDataEditor from '../components/agreement/DocumentDataEditor'
import { LineItemUnitTypeSelect } from '../components/agreement/LineItemUnitTypeSelect'
import { PricingPresentationFields } from '../components/agreement/PricingPresentationFields'
import MilestoneScheduleEditor from '../components/agreement/MilestoneScheduleEditor'
import {
  createEmptyMilestone,
  fetchContractMilestones,
  saveContractMilestones,
} from '../lib/milestoneBilling'
import { BillingMilestone } from '../types/agreement'
import {
  DSNY_PACKAGE_SERVICE_ID,
  applyDsnyApiDaysToRows,
  defaultDsnyScheduleRowsForKind,
  detectDsnyPackageKind,
  expandDsnyPackageServiceIdMap,
  findBuildingUpkeepItem,
  findDsnyPackageItem,
  findExteriorMaintenanceItem,
  isScheduleDerivedContractService,
  isDsnyScheduleValid,
  listCatalogItemsForCustomDsnyRows,
  rebaseDsnyRowsForPackageKind,
  removeDsnyPackagePair,
  removeLinkedLineItem,
  resolveLineItemRecurrenceForConsistency,
  resolveDsnyCombineTarget,
  dsnyRowNeedsCombineTarget,
  syncDsnyPackageInState,
  syncNonWeeklyDsnyLineItems,
  toUuidOrNull,
  upsertLinkedLineItem,
  type DsnyPackageKind,
  type DsnyCombineTarget,
} from '../lib/contractServiceLineItems'
import {
  DsnyVisitRepAssignment,
  visitTargetFromSelectValue,
  visitTargetSelectValue,
} from '../components/contract/DsnyVisitRepAssignment'
import {
  fetchCrewsAndEmployees,
  type CrewOption,
  type EmployeeOption,
} from '../api/jobWorkflow'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'
import {
  deriveDsnyContractArtifacts,
  hydrateDsnyScheduleRowsFromContractServices,
  buildDsnyVisitBoardServices,
  finalizeDsnyVisitGroupsAfterSeed,
  findDsnyPackageBoardId,
  ensureDsnyVisitGroupsForDisplay,
  replaceContractVisitGroups,
  buildDsnyChildItemsByType,
  syncVisitGroupAssigneesFromScheduleRows,
  preserveVisitGroupTargetAssignees,
  buildDsnyVisitGroupsFromSchedule,
  attachMissingNonWeeklyBoardServices,
} from '../lib/dsnyContractPersistence'

interface Contract {
  id: string
  contract_number: string
  title: string
  description?: string
  contract_type: 'one_time' | 'recurring' | 'as_needed'
  service_type?: string
  total_amount?: number
  billing_frequency?: string
  is_recurring: boolean
  recurrence_type?: string
  recurrence_days?: number[]
  dsny_integration?: boolean
  dsny_pickup_days?: string[]
  dsny_collection_types?: string[]
  interior_cleaning_schedule?: string[]
  // Comprehensive schedule system
  master_weekly_schedule?: number[]
  garbage_schedule?: number[]
  recycling_schedule?: number[]
  organics_schedule?: number[]
  bulk_schedule?: number[]
  status:
    | 'draft'
    | 'pending_signature'
    | 'signed'
    | 'in_process'
    | 'active'
    | 'paused'
    | 'completed'
    | 'cancelled'
    | 'expired'
  start_date?: string
  end_date?: string
  cancel_date?: string
  signed_date?: string
  payment_terms?: string
  late_fee_percentage?: number
  cancellation_terms?: string
  notes?: string
  created_at: string
  updated_at: string
  customer?: {
    company_name?: string
    contact_first_name: string
    contact_last_name: string
  }
  property?: {
    name: string
    address: string
    city?: string
    state?: string
  }
}

export function ContractEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingVisitGroups, setSavingVisitGroups] = useState(false)
  const [contract, setContract] = useState<Contract | null>(null)
  const [visitGroups, setVisitGroups] = useState<ContractVisitGroup[]>([])
  const [visitGroupsLoaded, setVisitGroupsLoaded] = useState(false)
  const [visitGroupsCustomized, setVisitGroupsCustomized] = useState(false)
  const { suggestGroupsDayCentric } = useCombinationRules()
  const { templates: visitGroupTemplates } = useVisitGroupTemplates()
  const [advancedVisitGroupId, setAdvancedVisitGroupId] = useState<string | null>(null)
  const [serviceCategories, setServiceCategories] = useState<any[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [properties, setProperties] = useState<any[]>([])
  const [proposals, setProposals] = useState<any[]>([])
  const [filteredProposals, setFilteredProposals] = useState<any[]>([])
  const [showCreateCustomer, setShowCreateCustomer] = useState(false)
  const [showCreateProperty, setShowCreateProperty] = useState(false)
  const [activeTab, setActiveTab] = useState<
    'overview' | 'services' | 'billing' | 'notes'
  >('overview')
  // Expanded state for the "Customize visits" board within the Services & Schedule tab
  const [showVisitBoard, setShowVisitBoard] = useState(false)

  // Scroll to top when tab changes
  useEffect(() => {
    // Scroll the main content area (from Layout component) to top
    const mainElement = document.querySelector('main')
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      // Fallback to window scroll if main element not found
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [activeTab])
  const [activities, setActivities] = useState<any[]>([])
  const [relatedItems, setRelatedItems] = useState<{
    jobs: any[]
    invoices: any[]
    proposals: any[]
  }>({ jobs: [], invoices: [], proposals: [] })
  const [notes, setNotes] = useState<Array<{ timestamp: string; note: string }>>([])
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [contractServices, setContractServices] = useState<any[]>([])
  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set())
  const [recurringScheduleType, setRecurringScheduleType] = useState<'maintenance_dsny' | 'other' | null>(null)

  // Store the proposal_line_item_id to link when DSNY schedule is created
  const [dsnyProposalLinkId, setDsnyProposalLinkId] = useState<string | null>(null)
  const [dsnyPackageKind, setDsnyPackageKind] = useState<DsnyPackageKind>('building_upkeep')

  // Dialog states for linking confirmations
  const [linkDialog, setLinkDialog] = useState<{
    show: boolean
    type: 'service-to-proposal' | 'line-item-to-service' | 'dsny-to-proposal'
    service?: any
    lineItem?: ContractLineItem
    matchingProposalItem?: any
    matchingService?: any
    onConfirm: () => void
    onCancel: () => void
  } | null>(null)

  const [maintenanceDSNYCategoryId, setMaintenanceDSNYCategoryId] = useState<string>('')
  const [crews, setCrews] = useState<CrewOption[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [showAddServiceModal, setShowAddServiceModal] = useState(false)
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null)
  const [serviceItems, setServiceItems] = useState<any[]>([])
  const [serviceItemsGrouped, setServiceItemsGrouped] = useState<ServiceItemGrouped[]>([])
  const [proposalLineItems, setProposalLineItems] = useState<any[]>([]) // Store proposal line items for linking

  // Add Service Modal state
  const [newService, setNewService] = useState({
    service_type: '',
    service_name: '',
    amount: 0,
    description: '',
    scheduled_date: '',
    scheduled_time: '',
    recurrence_type: 'none',
    recurrence_days: [] as number[],
    recurrence_day_of_month: null as number | null,
    recurrence_weekday: null as number | null,
    recurrence_occurrence: null as number | null,
    recurrence_month: null as number | null,
    recurrence_interval_months: 1,
    recurrence_interval_weeks: 1,
    recurrence_interval_days: 1,
    dsny_integration: false,
    garbage_schedule: [] as number[],
    recycling_schedule: [] as number[],
    organics_schedule: [] as number[],
    bulk_schedule: [] as number[],
    interior_cleaning_schedule: [] as number[],
  })

  // DSNY Schedule Rows for Maintenance DSNY recurring
  const [dsnyScheduleRows, setDsnyScheduleRows] = useState<Array<{
    id: string
    type: 'garbage' | 'recycling' | 'organics' | 'bulk' | 'hallway_cleaning' | 'custom'
    name: string
    days: number[]
    combine_with_dsny?: boolean
    combine_target?: DsnyCombineTarget
    assigned_crew_id?: string | null
    assigned_employee_id?: string | null
    service_item_id?: string
    // Recurrence override (custom + hallway rows only; pickup rows stay weekly)
    recurrence_type?: DsnyRowRecurrenceType
    monthly_mode?: DsnyMonthlyMode
    recurrence_weekday?: number | null
    recurrence_occurrence?: number | null
    recurrence_day_of_month?: number | null
    snap_to_closest_visit?: boolean
    persistedServiceId?: string
  }>>([])

  const contractServicesRef = useRef<any[]>([])
  const dsnyScheduleRowsRef = useRef<typeof dsnyScheduleRows>([])
  const lineItemsRef = useRef<any[]>([])

  useEffect(() => {
    contractServicesRef.current = contractServices
  }, [contractServices])

  useEffect(() => {
    dsnyScheduleRowsRef.current = dsnyScheduleRows
  }, [dsnyScheduleRows])

  useEffect(() => {
    let cancelled = false
    fetchCrewsAndEmployees().then(({ crews: crewOptions, employees: employeeOptions }) => {
      if (cancelled) return
      setCrews(crewOptions)
      setEmployees(employeeOptions)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Auto-fetch DSNY schedule when property is selected and rows are initialized
  const [dsnyScheduleFetched, setDsnyScheduleFetched] = useState(false)
  const [fetchingDSNY, setFetchingDSNY] = useState(false)
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  // Contract duration entry mode (UI-only; not persisted). 'months' derives the end
  // date from start + N months - 1 day; 'end_date' lets the user pick an exact end date.
  // Defaults to 'end_date' when editing since the month count is not stored.
  const [durationMode, setDurationMode] = useState<'months' | 'end_date'>('end_date')
  const [durationMonths, setDurationMonths] = useState(12)

  const toggleService = (serviceId: string) => {
    setExpandedServices((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId)
      } else {
        newSet.add(serviceId)
      }
      return newSet
    })
  }

  // Day names for schedule checkboxes
  const dayNames = [
    { key: 0, label: 'Sunday' },
    { key: 1, label: 'Monday' },
    { key: 2, label: 'Tuesday' },
    { key: 3, label: 'Wednesday' },
    { key: 4, label: 'Thursday' },
    { key: 5, label: 'Friday' },
    { key: 6, label: 'Saturday' },
  ]

  const toggleDayForRow = (rowId: string, day: number) => {
    setDsnyScheduleRows((rows) =>
      rows.map((row) => {
        if (row.id !== rowId) return row
        const days = Array.isArray(row.days) ? row.days : []
        return {
          ...row,
          days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort(),
        }
      })
    )
  }

  const addDsnyScheduleRow = () => {
    setDsnyScheduleRows([
      ...dsnyScheduleRows,
      {
        id: `custom_${Date.now()}`,
        type: 'custom',
        name: '',
        days: [],
        // No visit target until the user chooses — do not default to Separate.
        combine_with_dsny: false,
        service_item_id: '',
        recurrence_type: 'weekly',
        monthly_mode: 'nth_weekday',
        recurrence_weekday: null,
        recurrence_occurrence: null,
        recurrence_day_of_month: null,
        snap_to_closest_visit: false,
      },
    ])
  }

  const removeDsnyScheduleRow = (rowId: string) => {
    setDsnyScheduleRows(rows => rows.filter(row => row.id !== rowId))
  }

  const updateDsnyScheduleRowName = (rowId: string, name: string) => {
    setDsnyScheduleRows(rows =>
      rows.map(row => (row.id === rowId ? { ...row, name } : row))
    )
  }

  const updateDsnyScheduleRow = (
    rowId: string,
    patch: Partial<{
      name: string
      service_item_id: string
      recurrence_type: DsnyRowRecurrenceType
      monthly_mode: DsnyMonthlyMode
      recurrence_weekday: number | null
      recurrence_occurrence: number | null
      recurrence_day_of_month: number | null
      snap_to_closest_visit: boolean
      combine_with_dsny: boolean
      combine_target: DsnyCombineTarget | undefined
      assigned_crew_id: string | null
      assigned_employee_id: string | null
    }>
  ) => {
    setDsnyScheduleRows((rows) =>
      rows.map((row) => {
        if (row.id !== rowId) return row
        const next = { ...row, ...patch }
        if (Object.prototype.hasOwnProperty.call(patch, 'combine_target')) {
          next.combine_with_dsny = patch.combine_target === 'dsny'
        }
        return next
      })
    )
  }

  const updateDsnyRowAssignments = (
    rowIds: string[],
    patch:
      | { assigned_crew_id: string | null }
      | { assigned_employee_id: string | null }
  ) => {
    const serviceIds = new Set(
      dsnyScheduleRows
        .filter((row) => rowIds.includes(row.id))
        .map((row) => row.persistedServiceId || `dsny_${row.id}`)
    )
    setDsnyScheduleRows((rows) =>
      rows.map((row) => (rowIds.includes(row.id) ? { ...row, ...patch } : row))
    )
    setVisitGroups((groups) =>
      groups.map((group) =>
        getGroupAssignments(group).some((assignment) => serviceIds.has(assignment.service_id))
          ? { ...group, ...patch }
          : group
      )
    )
  }

  const selectCustomDsnyCatalogItem = (rowId: string, serviceItemId: string) => {
    const options = listCatalogItemsForCustomDsnyRows(serviceItemsGrouped)
    const item = options.find((o) => o.id === serviceItemId)
    updateDsnyScheduleRow(rowId, {
      service_item_id: serviceItemId || '',
      name: item?.name || '',
    })
  }

  const customDsnyCatalogOptions = listCatalogItemsForCustomDsnyRows(serviceItemsGrouped)

  // Line items for billing
  interface ContractLineItem {
    id: string
    service_item_id?: string
    proposal_line_item_id?: string // Links back to proposal line item for reporting
    contract_service_id?: string // Backend link to contract_services table
    description: string
    quantity: number
    is_variable: boolean // Checkbox for variable items (per hour, per unit, etc.)
    unit_type: string
    unit_price: number
    total_price: number
    sort_order: number
    linked_service_id?: string // Frontend link to service in Services & Schedule tab (for display/mapping)
  }

  const [lineItems, setLineItems] = useState<ContractLineItem[]>([])
  lineItemsRef.current = lineItems
  const [billingMilestones, setBillingMilestones] = useState<BillingMilestone[]>([])

  const [showServiceSelectionModal, setShowServiceSelectionModal] = useState(false)
  const [selectedServicesForImport, setSelectedServicesForImport] = useState<Set<string>>(new Set())

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    template_type: '',
    contract_type: 'one_time',
    service_type: '',
    customer_id: '',
    property_id: '',
    proposal_id: '',
    parent_contract_id: '', // For contract renewals
    original_contract_id: '', // Original contract in renewal chain
    total_amount: 0, // Per-period amount or total if one-time
    contract_total_amount: 0, // Total contract value (for divided billing)
    billing_frequency: 'monthly',
    billing_day_of_month: 1,
    billing_method: 'per_period' as 'per_period' | 'divide_total' | 'actual_usage',
    is_recurring: false,
    recurrence_type: 'weekly',
    recurrence_interval: 1,
    recurrence_days: [] as number[],
    recurrence_end_date: '',
    recurrence_end_count: 0,
    status: 'draft' as
      | 'draft'
      | 'pending_signature'
      | 'signed'
      | 'in_process'
      | 'active'
      | 'paused'
      | 'completed'
      | 'cancelled'
      | 'expired',
    start_date: '',
    end_date: '',
    cancel_date: '',
    payment_terms: 'Net 30',
    late_fee_percentage: 0,
    sales_tax_status: '' as '' | 'taxable' | 'exempt',
    cancellation_terms: '',
    notes: '',
    // Comprehensive Schedule System
    master_weekly_schedule: [] as number[], // Days of week we visit (0-6)
    garbage_schedule: [] as number[], // Days for garbage prep
    recycling_schedule: [] as number[], // Days for recycling prep
    bulk_schedule: [] as number[], // Days for bulk prep
    organics_schedule: [] as number[], // Days for organics prep
    interior_cleaning_schedule: [] as number[], // Days for interior cleaning
    // Dynamic manual schedules
    manual_schedules: [] as Array<{
      id: string
      name: string
      description: string
      color: string
      days: number[]
    }>,
    dsny_integration: false,
    // Legacy fields for compatibility
    dsny_pickup_days: [] as string[],
    dsny_collection_types: [] as string[],
    // Phase 3: payment method
    payment_method: '' as '' | 'cash' | 'check' | 'ach' | 'credit_card',
    // Document generation
    scope_of_work: '',
    document_data: {} as DocumentData,
    assignment_source: '' as AssignmentSource | '',
    default_assigned_crew_id: '',
    default_assigned_employee_id: '',
  })
  const [scopeType, setScopeType] = useState<ContractScopeType>('single_property')
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([])

  useEffect(() => {
    if (formData.contract_type === 'recurring' && scopeType !== 'single_property') {
      setScopeType('single_property')
      setSelectedPropertyIds(formData.property_id ? [formData.property_id] : [])
    }
  }, [formData.contract_type, formData.property_id, scopeType])

  const [propertyAssignmentLabels, setPropertyAssignmentLabels] = useState<{
    crewName: string | null
    employeeName: string | null
  } | null>(null)

  useEffect(() => {
    if (id) {
      setVisitGroupsLoaded(false)
      setDsnyScheduleFetched(false)
      fetchContract(id)
      fetchContractLineItems(id)
      fetchActivities()
    }
    fetchData()
    fetchServiceItems()
  }, [id])

  // In "months" duration mode, derive end_date = start_date + N months - 1 day.
  // In "end_date" mode the user picks the end date directly (no auto-derivation).
  useEffect(() => {
    if (durationMode !== 'months' || !formData.start_date) return
    const endDateString = computeEndDateFromMonths(formData.start_date, durationMonths)
    if (!endDateString) return
    setFormData((prev) => (prev.end_date === endDateString ? prev : { ...prev, end_date: endDateString }))
  }, [durationMode, durationMonths, formData.start_date])

  useEffect(() => {
    let cancelled = false
    const loadPropertyAssignmentLabels = async () => {
      if (!formData.property_id) {
        setPropertyAssignmentLabels(null)
        return
      }
      const { data: prop } = await supabase
        .from('properties')
        .select('default_assigned_crew_id, default_assigned_employee_id')
        .eq('id', formData.property_id)
        .maybeSingle()
      if (cancelled || !prop) {
        if (!cancelled) setPropertyAssignmentLabels({ crewName: null, employeeName: null })
        return
      }
      const [crewRes, empRes] = await Promise.all([
        prop.default_assigned_crew_id
          ? supabase.from('crews').select('name').eq('id', prop.default_assigned_crew_id).maybeSingle()
          : Promise.resolve({ data: null as { name: string } | null }),
        prop.default_assigned_employee_id
          ? supabase
              .from('employees')
              .select('first_name, last_name')
              .eq('id', prop.default_assigned_employee_id)
              .maybeSingle()
          : Promise.resolve({ data: null as { first_name: string; last_name: string } | null }),
      ])
      if (cancelled) return
      setPropertyAssignmentLabels({
        crewName: crewRes.data?.name || null,
        employeeName: empRes.data
          ? `${empRes.data.first_name || ''} ${empRes.data.last_name || ''}`.trim()
          : null,
      })
    }
    loadPropertyAssignmentLabels()
    return () => {
      cancelled = true
    }
  }, [formData.property_id])

  const fetchServiceItems = async () => {
    const items = await fetchServiceItemsGrouped()
    setServiceItemsGrouped(items)
    // Also populate flat serviceItems array for easier lookup
    const flatItems: any[] = []
    items.forEach(group => {
      group.items.forEach(item => {
        flatItems.push(item)
      })
    })
    setServiceItems(flatItems)
  }

  const isMaintenanceServiceById = (serviceItemId: string) => {
    const selectedItem = serviceItems.find((item) => item.id === serviceItemId)
    if (!selectedItem) return false
    if (!selectedItem.categories) return false
    if (!maintenanceDSNYCategoryId) return false
    return selectedItem.service_category_id === maintenanceDSNYCategoryId
  }

  const handleEditService = (serviceId: string) => {
    const serviceToEdit = contractServices.find(s => s.id === serviceId)
    if (serviceToEdit) {
      const sched = (serviceToEdit as any).schedule || {}
      setNewService({
        service_type: serviceToEdit.service_item_id || '',
        service_name: serviceToEdit.service_name || '',
        amount: serviceToEdit.amount || 0,
        description: serviceToEdit.description || serviceToEdit.service_name || '',
        scheduled_date: serviceToEdit.scheduled_date || '',
        scheduled_time: serviceToEdit.scheduled_time || '',
        recurrence_type:
          sched.recurrence_type ||
          (serviceToEdit as any).recurrence_type ||
          (formData.contract_type === 'one_time' ? 'none' : 'weekly'),
        recurrence_days: sched.recurrence_days || (serviceToEdit as any).recurrence_days || [],
        recurrence_day_of_month:
          sched.recurrence_day_of_month ?? (serviceToEdit as any).recurrence_day_of_month ?? null,
        recurrence_weekday: sched.recurrence_weekday ?? (serviceToEdit as any).recurrence_weekday ?? null,
        recurrence_occurrence:
          sched.recurrence_occurrence ?? (serviceToEdit as any).recurrence_occurrence ?? null,
        recurrence_month: sched.recurrence_month ?? (serviceToEdit as any).recurrence_month ?? null,
        recurrence_interval_months:
          sched.recurrence_interval_months ?? (serviceToEdit as any).recurrence_interval_months ?? 1,
        recurrence_interval_weeks:
          sched.recurrence_interval_weeks ?? (serviceToEdit as any).recurrence_interval_weeks ?? 1,
        recurrence_interval_days:
          sched.recurrence_interval_days ?? (serviceToEdit as any).recurrence_interval_days ?? 1,
        dsny_integration:
          sched.dsny_integration || (serviceToEdit as any).dsny_integration || false,
        garbage_schedule: sched.garbage_schedule || (serviceToEdit as any).garbage_schedule || [],
        recycling_schedule: sched.recycling_schedule || (serviceToEdit as any).recycling_schedule || [],
        organics_schedule: sched.organics_schedule || (serviceToEdit as any).organics_schedule || [],
        bulk_schedule: sched.bulk_schedule || (serviceToEdit as any).bulk_schedule || [],
        interior_cleaning_schedule:
          sched.interior_cleaning_schedule ||
          (serviceToEdit as any).interior_cleaning_schedule ||
          [],
      })
      setEditingServiceId(serviceId)
      setShowAddServiceModal(true)
    }
  }

  /** True when a recurring service still needs cadence (type and/or days) before crew visits can be derived. */
  const serviceNeedsRecurringSchedule = (service: any): boolean => {
    if (formData.contract_type !== 'recurring') return false
    const rtype = service.schedule?.recurrence_type || service.recurrence_type
    if (!rtype || rtype === 'none' || rtype === 'one_time') return true
    if (rtype === 'weekly' || rtype === 'bi_weekly') {
      const days = service.schedule?.recurrence_days || service.recurrence_days || []
      return !(Array.isArray(days) && days.length > 0)
    }
    if (rtype === 'monthly') {
      const hasDom =
        (service.schedule?.recurrence_day_of_month ?? service.recurrence_day_of_month) != null
      const hasNth =
        (service.schedule?.recurrence_weekday ?? service.recurrence_weekday) != null &&
        (service.schedule?.recurrence_occurrence ?? service.recurrence_occurrence) != null
      return !hasDom && !hasNth
    }
    return false
  }

  const openOtherRecurringSchedule = () => {
    setRecurringScheduleType('other')
    const firstIncomplete = contractServices.find((s) => serviceNeedsRecurringSchedule(s))
    if (firstIncomplete) {
      window.setTimeout(() => handleEditService(firstIncomplete.id), 0)
    }
  }

  // Handle selecting DSNY schedule type - check for matching proposal line items
  const handleSelectDSNYSchedule = () => {
    const buildingUpkeepItem = findBuildingUpkeepItem(serviceItemsGrouped)
    const exteriorMaintenanceItem = findExteriorMaintenanceItem(serviceItemsGrouped)
    const packageItem =
      findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped) ||
      buildingUpkeepItem ||
      exteriorMaintenanceItem

    let matchingProposalItem: any = null

    if (proposalLineItems.length > 0) {
      const packageIds = [buildingUpkeepItem?.id, exteriorMaintenanceItem?.id].filter(
        Boolean
      ) as string[]
      matchingProposalItem = proposalLineItems.find(
        (propItem: any) =>
          propItem.service_item_id && packageIds.includes(propItem.service_item_id)
      )

      if (!matchingProposalItem) {
        matchingProposalItem = proposalLineItems.find((propItem: any) => {
          const desc = (propItem.description || '').toLowerCase().trim()
          return (
            desc.includes('building upkeep') ||
            desc.includes('building maintenance') ||
            desc.includes('exterior maintenance') ||
            desc === 'building upkeep' ||
            desc === 'building maintenance'
          )
        })
      }

      if (matchingProposalItem) {
        setDsnyPackageKind(
          detectDsnyPackageKind(
            {
              service_item_id: matchingProposalItem.service_item_id,
              service_name: matchingProposalItem.description,
            },
            serviceItemsGrouped
          )
        )
      }
    }

    const linkPackageItem =
      (matchingProposalItem &&
        (matchingProposalItem.service_item_id === exteriorMaintenanceItem?.id
          ? exteriorMaintenanceItem
          : matchingProposalItem.service_item_id === buildingUpkeepItem?.id
            ? buildingUpkeepItem
            : packageItem)) ||
      packageItem

    if (matchingProposalItem && linkPackageItem) {
      setLinkDialog({
        show: true,
        type: 'dsny-to-proposal',
        service: {
          id: 'dsny_package',
          service_item_id: linkPackageItem.id,
          service_name: linkPackageItem.name,
        },
        matchingProposalItem: matchingProposalItem,
        onConfirm: () => {
          setRecurringScheduleType('maintenance_dsny')
          setDsnyProposalLinkId(matchingProposalItem.id)
          setLinkDialog(null)
        },
        onCancel: () => {
          setRecurringScheduleType('maintenance_dsny')
          setDsnyProposalLinkId(null)
          setLinkDialog(null)
        },
      })
    } else {
      setRecurringScheduleType('maintenance_dsny')
      setDsnyProposalLinkId(null)
    }
  }

  const handleRemoveService = (serviceId: string) => {
    setContractServices(contractServices.filter((s) => s.id !== serviceId))
    setLineItems(removeLinkedLineItem(lineItems, serviceId))
  }

  const handleAddService = () => {
    if (!newService.service_type || !newService.service_name) {
      alert('Please select a service item')
      return
    }

    // For recurring contracts, require a recurrence type
    if (formData.contract_type === 'recurring' && (newService.recurrence_type === 'none' || !newService.recurrence_type)) {
      alert('Please select a recurrence type for the service')
      return
    }

    // Recurring "Other" path: require days/cadence so schedule setup is complete in-flow
    if (
      formData.contract_type === 'recurring' &&
      recurringScheduleType === 'other' &&
      newService.recurrence_type !== 'none'
    ) {
      const rtype = newService.recurrence_type
      if (
        (rtype === 'weekly' || rtype === 'bi_weekly') &&
        (!newService.recurrence_days || newService.recurrence_days.length === 0)
      ) {
        alert('Please select at least one day for the recurring schedule')
        return
      }
      if (rtype === 'monthly') {
        const hasDom = newService.recurrence_day_of_month != null
        const hasNth =
          newService.recurrence_weekday != null && newService.recurrence_occurrence != null
        if (!hasDom && !hasNth) {
          alert('Please set a monthly schedule (day of month or weekday occurrence)')
          return
        }
      }
    }

    // If editing, update existing service; otherwise add new
    if (editingServiceId) {
      const updatedServices = contractServices.map(s => {
        if (s.id === editingServiceId) {
          const selectedItem = serviceItems.find((item) => item.id === newService.service_type)
          const selectedCategory = serviceCategories.find(
            (c) => c.id === selectedItem?.service_category_id
          )
          const isMaintenance = selectedCategory?.name?.toLowerCase().includes('dsny') || false
          const shouldEnableDSNY = recurringScheduleType === 'maintenance_dsny' || (newService.dsny_integration && isMaintenance)

          return {
            ...s,
            service_item_id: newService.service_type,
            service_type: selectedItem?.service_category_id || '',
            service_name: newService.service_name,
            amount: newService.amount || 0,
            description: newService.description || newService.service_name,
            scheduled_date: formData.contract_type === 'one_time' ? newService.scheduled_date : null,
            scheduled_time: formData.contract_type === 'one_time' ? newService.scheduled_time : null,
            is_maintenance: isMaintenance || recurringScheduleType === 'maintenance_dsny',
            schedule: {
              recurrence_type: formData.contract_type === 'one_time' ? null : (newService.recurrence_type === 'none' ? null : newService.recurrence_type),
              recurrence_days: formData.contract_type === 'one_time' ? [] : ((newService.recurrence_type === 'none') ? [] : newService.recurrence_days),
              recurrence_day_of_month: newService.recurrence_day_of_month,
              recurrence_weekday: newService.recurrence_weekday,
              recurrence_occurrence: newService.recurrence_occurrence,
              recurrence_month: newService.recurrence_month,
              recurrence_interval_months: newService.recurrence_interval_months,
              recurrence_interval_weeks: newService.recurrence_interval_weeks,
              recurrence_interval_days: newService.recurrence_interval_days,
              dsny_integration: shouldEnableDSNY,
              garbage_schedule: newService.garbage_schedule || [],
              recycling_schedule: newService.recycling_schedule || [],
              organics_schedule: newService.organics_schedule || [],
              bulk_schedule: newService.bulk_schedule || [],
              interior_cleaning_schedule: newService.interior_cleaning_schedule || [],
            },
          }
        }
        return s
      })
      setContractServices(updatedServices)
      const updatedService = updatedServices.find((s) => s.id === editingServiceId)
      if (updatedService && updatedService.id !== DSNY_PACKAGE_SERVICE_ID) {
        setLineItems((prev) => upsertLinkedLineItem(prev, updatedService))
      }
      setEditingServiceId(null)
    } else {
      const selectedItem = serviceItems.find((item) => item.id === newService.service_type)
      const selectedCategory = serviceCategories.find(
        (c) => c.id === selectedItem?.service_category_id
      )
      const isMaintenance = selectedCategory?.name?.toLowerCase().includes('dsny') || false
      const shouldEnableDSNY = recurringScheduleType === 'maintenance_dsny' || (newService.dsny_integration && isMaintenance)

      const service = {
        id: `service_${Date.now()}`,
        service_item_id: newService.service_type,
        service_type: selectedItem?.service_category_id || '',
        service_name: newService.service_name,
        amount: newService.amount || 0,
        description: newService.description || newService.service_name,
        scheduled_date: formData.contract_type === 'one_time' ? newService.scheduled_date : null,
        scheduled_time: formData.contract_type === 'one_time' ? newService.scheduled_time : null,
        is_maintenance: isMaintenance || recurringScheduleType === 'maintenance_dsny',
        schedule: {
          recurrence_type: formData.contract_type === 'one_time' ? null : (newService.recurrence_type === 'none' ? null : newService.recurrence_type),
          recurrence_days: formData.contract_type === 'one_time' ? [] : ((newService.recurrence_type === 'none') ? [] : newService.recurrence_days),
          recurrence_day_of_month: newService.recurrence_day_of_month,
          recurrence_weekday: newService.recurrence_weekday,
          recurrence_occurrence: newService.recurrence_occurrence,
          recurrence_month: newService.recurrence_month,
          recurrence_interval_months: newService.recurrence_interval_months,
          recurrence_interval_days: newService.recurrence_interval_days,
          dsny_integration: shouldEnableDSNY,
          garbage_schedule: newService.garbage_schedule || [],
          recycling_schedule: newService.recycling_schedule || [],
          organics_schedule: newService.organics_schedule || [],
          bulk_schedule: newService.bulk_schedule || [],
          interior_cleaning_schedule: newService.interior_cleaning_schedule || [],
        },
      }

      setContractServices([...contractServices, service])
      setLineItems((prev) => upsertLinkedLineItem(prev, service))
      void (async () => {
        const packageItemId = service.service_item_id
        if (!packageItemId) return
        const local = serviceItems.find((i) => i.id === packageItemId) as any
        let isPackage = !!local?.is_package
        if (!isPackage) {
          const { data } = await supabase
            .from('service_items')
            .select('is_package')
            .eq('id', packageItemId)
            .maybeSingle()
          isPackage = !!data?.is_package
        }
        if (!isPackage) return
        const children = await fetchPackageChildItems(packageItemId)
        if (children.length === 0) return
        const childServices = buildPackageChildServices(children, {
          packageServiceId: service.id,
          serviceType: service.service_type,
          recurrence: {
            recurrence_type: service.schedule?.recurrence_type,
            recurrence_days: service.schedule?.recurrence_days || [],
          },
        })
        setContractServices((prev) => [...prev, ...childServices])
      })()
    }

    // Reset form
    const defaultRecurrenceType = formData.contract_type === 'one_time' ? 'none' : 'weekly'
    const isMaintenanceDSNY = recurringScheduleType === 'maintenance_dsny'
    setNewService({
      service_type: '',
      service_name: '',
      amount: 0,
      description: '',
      scheduled_date: '',
      scheduled_time: '',
      recurrence_type: defaultRecurrenceType,
      recurrence_days: [],
      recurrence_day_of_month: null,
      recurrence_weekday: null,
      recurrence_occurrence: null,
      recurrence_month: null,
      recurrence_interval_months: 1,
      recurrence_interval_weeks: 1,
      recurrence_interval_days: 1,
      dsny_integration: isMaintenanceDSNY,
      garbage_schedule: [],
      recycling_schedule: [],
      organics_schedule: [],
      bulk_schedule: [],
      interior_cleaning_schedule: [],
    })
    setEditingServiceId(null)
    setShowAddServiceModal(false)
  }

  useEffect(() => {
    if (formData.customer_id) {
      const customerProposals = proposals.filter((p) => p.customer_id === formData.customer_id)
      setFilteredProposals(customerProposals)
    } else {
      setFilteredProposals([])
    }
  }, [formData.customer_id, proposals])

  // Initialize newService state when modal opens for recurring contracts
  useEffect(() => {
    if (showAddServiceModal && !editingServiceId && formData.contract_type === 'recurring') {
      setNewService(prev => {
        // Only initialize if it's still set to 'none' (default initial state)
        if (prev.recurrence_type === 'none') {
          return {
            ...prev,
            recurrence_type: 'weekly',
          }
        }
        return prev
      })
    }
  }, [showAddServiceModal, editingServiceId, formData.contract_type])

  // Update total amount and contract total amount when line items change
  useEffect(() => {
    const lineItemsTotal = lineItems.reduce((sum, item) => sum + item.total_price, 0)
    // Update contract_total_amount from line items
    if (lineItemsTotal > 0) {
      setFormData((prev) => ({
        ...prev,
        contract_total_amount: lineItemsTotal
      }))
    }
  }, [lineItems])

  // Automatically calculate amount per invoice when billing method is "divide_total"
  useEffect(() => {
    if (formData.billing_method === 'divide_total' &&
        formData.billing_frequency !== 'one_time' &&
        formData.billing_frequency !== 'after_each_service') {
      const periods = calculateBillingPeriods()
      if (periods > 0 && formData.contract_total_amount > 0) {
        setFormData((prev) => ({
          ...prev,
          total_amount: prev.contract_total_amount / periods
        }))
      } else if (formData.contract_total_amount === 0) {
        // Reset to 0 if contract total is 0
        setFormData((prev) => ({
          ...prev,
          total_amount: 0
        }))
      }
    } else if (formData.billing_method === 'per_period') {
      // For per_period, use contract_total_amount as total_amount
      setFormData((prev) => ({
        ...prev,
        total_amount: prev.contract_total_amount
      }))
    }
    // Note: 'actual_usage' method doesn't need total_amount calculation
  }, [formData.billing_method, formData.billing_frequency, formData.contract_total_amount, formData.start_date, formData.end_date])

  // Initialize blank DSNY rows only for new schedule entry — not when loading from DB.
  useEffect(() => {
    if (
      recurringScheduleType === 'maintenance_dsny' &&
      dsnyScheduleRows.length === 0 &&
      !dsnyScheduleFetched
    ) {
      setDsnyScheduleRows(
        defaultDsnyScheduleRowsForKind(dsnyPackageKind) as typeof dsnyScheduleRows
      )
      setDsnyScheduleFetched(false) // Reset fetch flag when initializing
    } else if (recurringScheduleType !== 'maintenance_dsny') {
      // Clear rows when switching away from maintenance_dsny
      setDsnyScheduleRows([])
      setDsnyScheduleFetched(false)
    }
  }, [recurringScheduleType, dsnyScheduleFetched, dsnyScheduleRows.length, dsnyPackageKind])

  // Sync DSNY package service + linked catalogue package line item when schedule is configured.
  // Uses refs for services/line items so day toggles (Building Upkeep or Exterior) do not
  // retrigger this effect from their own setState.
  useEffect(() => {
    const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
    const syncOptions = {
      buildingUpkeepItem: packageItem,
      maintenanceDSNYCategoryId,
      proposalLineItemId: dsnyProposalLinkId,
    }

    if (recurringScheduleType !== 'maintenance_dsny') {
      const cleared = removeDsnyPackagePair(
        contractServicesRef.current,
        lineItemsRef.current
      )
      if (JSON.stringify(cleared.contractServices) !== JSON.stringify(contractServicesRef.current)) {
        setContractServices(cleared.contractServices)
      }
      if (JSON.stringify(cleared.lineItems) !== JSON.stringify(lineItemsRef.current)) {
        setLineItems(cleared.lineItems)
      }
      return
    }

    if (!isDsnyScheduleValid(dsnyScheduleRows)) return

    const synced = syncDsnyPackageInState(
      contractServicesRef.current,
      lineItemsRef.current,
      dsnyScheduleRows,
      syncOptions
    )

    if (JSON.stringify(synced.contractServices) !== JSON.stringify(contractServicesRef.current)) {
      setContractServices(synced.contractServices)
    }
    if (JSON.stringify(synced.lineItems) !== JSON.stringify(lineItemsRef.current)) {
      setLineItems(synced.lineItems)
    }
  }, [
    recurringScheduleType,
    dsnyScheduleRows,
    maintenanceDSNYCategoryId,
    dsnyProposalLinkId,
    serviceItemsGrouped,
    dsnyPackageKind,
  ])

  const fetchData = async () => {
    try {
      // Fetch customers
      const { data: customersData } = await supabase
        .from('customers')
        .select('id, company_name, contact_first_name, contact_last_name')
        .order('company_name')

      // Fetch properties
      const { data: propertiesData } = await supabase
        .from('properties')
        .select('id, name, address, city, state, customer_id')
        .order('name')

      // Fetch proposals
      const { data: proposalsData } = await supabase
        .from('proposals')
        .select('id, title, proposal_number, customer_id, status')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })

      // Fetch service categories
      const { data: serviceCategoriesData } = await supabase
        .from('service_categories')
        .select('id, name, description, operational_division_id')
        .eq('is_active', true)
        .order('name')

      setCustomers(customersData || [])
      setProperties(propertiesData || [])
      setProposals(proposalsData || [])
      setServiceCategories(serviceCategoriesData || [])
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name, description, operational_division_id')
        .eq('is_active', true)
        .order('name')

      if (error) throw error
      setServiceCategories(data || [])

      // Find and set Maintenance - DSNY category ID
      const maintenanceDSNY = data?.find(
        (cat) => cat.name.toLowerCase() === 'maintenance - dsny'
      )
      if (maintenanceDSNY) {
        console.log('Found Maintenance - DSNY category with ID:', maintenanceDSNY.id)
        setMaintenanceDSNYCategoryId(maintenanceDSNY.id)
      }
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  const fetchContractServices = async (
    contractId: string,
    opts?: { contractType?: string | null; isDsnyContract?: boolean }
  ) => {
    try {
      const { data, error } = await supabase
        .from('contract_services')
        .select('*')
        .eq('contract_id', contractId)

      if (error) throw error

      // DB rows store cadence on top-level columns; in-memory / wizard rows nest under
      // `schedule`. Normalize so edit modals and "needs schedule" checks see both.
      const normalizedServices = (data || []).map((s: any) => {
        const schedule = {
          ...(typeof s.schedule === 'object' && s.schedule ? s.schedule : {}),
          recurrence_type: s.schedule?.recurrence_type || s.recurrence_type || null,
          recurrence_days: s.schedule?.recurrence_days || s.recurrence_days || [],
          recurrence_day_of_month:
            s.schedule?.recurrence_day_of_month ?? s.recurrence_day_of_month ?? null,
          recurrence_weekday: s.schedule?.recurrence_weekday ?? s.recurrence_weekday ?? null,
          recurrence_occurrence:
            s.schedule?.recurrence_occurrence ?? s.recurrence_occurrence ?? null,
          recurrence_month: s.schedule?.recurrence_month ?? s.recurrence_month ?? null,
          recurrence_interval_months:
            s.schedule?.recurrence_interval_months ?? s.recurrence_interval_months ?? 1,
          recurrence_interval_weeks:
            s.schedule?.recurrence_interval_weeks ?? s.recurrence_interval_weeks ?? 1,
          recurrence_interval_days:
            s.schedule?.recurrence_interval_days ?? s.recurrence_interval_days ?? 1,
          dsny_integration: !!(s.schedule?.dsny_integration || s.dsny_integration),
          garbage_schedule: s.schedule?.garbage_schedule || s.garbage_schedule || [],
          recycling_schedule: s.schedule?.recycling_schedule || s.recycling_schedule || [],
          organics_schedule: s.schedule?.organics_schedule || s.organics_schedule || [],
          bulk_schedule: s.schedule?.bulk_schedule || s.bulk_schedule || [],
          interior_cleaning_schedule:
            s.schedule?.interior_cleaning_schedule || s.interior_cleaning_schedule || [],
        }
        return { ...s, schedule, dsny_integration: !!schedule.dsny_integration }
      })

      let dsnyRows: typeof dsnyScheduleRows = []
      if (normalizedServices.length > 0) {
        const hasDSNY = normalizedServices.some(
          (s: any) => s.dsny_integration || s.schedule?.dsny_integration
        )
        const contractIsRecurring =
          opts?.contractType === 'recurring' || formData.contract_type === 'recurring'
        if (hasDSNY || opts?.isDsnyContract) {
          setRecurringScheduleType('maintenance_dsny')

          const { data: groups } = await supabase
            .from('contract_visit_groups')
            .select(
              'id, name, dsny_integration, assigned_crew_id, assigned_employee_id'
            )
            .eq('contract_id', contractId)

          let mappings: Parameters<typeof hydrateDsnyScheduleRowsFromContractServices>[1] = []
          const groupById = new Map(
            (groups || []).map(
              (g: {
                id: string
                name: string
                dsny_integration: boolean
                assigned_crew_id?: string | null
                assigned_employee_id?: string | null
              }) => [g.id, g]
            )
          )
          const groupIds = Array.from(groupById.keys())
          if (groupIds.length > 0) {
            const { data: mappingRows } = await supabase
              .from('contract_visit_group_services')
              .select(
                'visit_group_id, contract_service_id, schedule_mode, recurrence_type, recurrence_weekday, recurrence_occurrence, recurrence_day_of_month, recurrence_nearest_visit'
              )
              .in('visit_group_id', groupIds)
            mappings = (mappingRows || []).map(
              (m: {
                visit_group_id: string
                contract_service_id: string
                schedule_mode?: string
                recurrence_type?: string | null
                recurrence_weekday?: number | null
                recurrence_occurrence?: number | null
                recurrence_day_of_month?: number | null
                recurrence_nearest_visit?: boolean | null
              }) => {
                const host = groupById.get(m.visit_group_id)
                return {
                  contract_service_id: m.contract_service_id,
                  schedule_mode: m.schedule_mode,
                  recurrence_type: m.recurrence_type,
                  recurrence_weekday: m.recurrence_weekday,
                  recurrence_occurrence: m.recurrence_occurrence,
                  recurrence_day_of_month: m.recurrence_day_of_month,
                  recurrence_nearest_visit: m.recurrence_nearest_visit,
                  visit_group_name: host?.name ?? null,
                  visit_group_dsny_integration: !!host?.dsny_integration,
                }
              }
            )
          }

          const detectedKind = detectDsnyPackageKind(
            normalizedServices.find(
              (s: any) =>
                s.dsny_integration &&
                (Number(s.amount) > 0 ||
                  String(s.service_name || '')
                    .toLowerCase()
                    .includes('upkeep') ||
                  String(s.service_name || '')
                    .toLowerCase()
                    .includes('exterior maintenance'))
            ),
            serviceItemsGrouped
          )
          setDsnyPackageKind(detectedKind)
          dsnyRows = hydrateDsnyScheduleRowsFromContractServices(normalizedServices, mappings, {
            packageKind: detectedKind,
          }) as typeof dsnyScheduleRows
          // Assignees live on visit groups — stamp them onto schedule rows early so
          // Exterior/Interior dropdowns do not flash Inherit before fetchVisitGroups.
          const exteriorHost = (groups || []).find(
            (g: any) => g.dsny_integration && (g.assigned_crew_id || g.assigned_employee_id)
          ) || (groups || []).find((g: any) => g.dsny_integration)
          const interiorHost = (groups || []).find(
            (g: any) =>
              /^Hallway Cleaning\b/i.test(g.name || '') &&
              (g.assigned_crew_id || g.assigned_employee_id)
          ) || (groups || []).find((g: any) => /^Hallway Cleaning\b/i.test(g.name || ''))
          if (exteriorHost || interiorHost) {
            dsnyRows = dsnyRows.map((row) => {
              const target = resolveDsnyCombineTarget(row as any)
              const host = target === 'dsny' ? exteriorHost : target === 'hallway' ? interiorHost : null
              if (!host) return row
              return {
                ...row,
                assigned_crew_id: host.assigned_crew_id || row.assigned_crew_id || null,
                assigned_employee_id:
                  host.assigned_employee_id || row.assigned_employee_id || null,
              }
            }) as typeof dsnyScheduleRows
          }
          setDsnyScheduleRows(dsnyRows)
          setDsnyScheduleFetched(true)
        } else if (contractIsRecurring) {
          setRecurringScheduleType('other')
        }
      }

      contractServicesRef.current = normalizedServices
      setContractServices(normalizedServices)
      dsnyScheduleRowsRef.current = dsnyRows
      return { services: normalizedServices, dsnyRows }
    } catch (error) {
      console.error('Error fetching contract services:', error)
      return { services: [] as any[], dsnyRows: [] as typeof dsnyScheduleRows }
    }
  }

  const fetchContractLineItems = async (contractId: string) => {
    try {
      const { data, error } = await supabase
        .from('contract_line_items')
        .select('*')
        .eq('contract_id', contractId)
        .order('sort_order', { ascending: true })

      if (error) {
        // Table might not exist yet, that's okay
        console.warn('Error fetching contract line items (table may not exist):', error)
        return
      }

      if (data && data.length > 0) {
        setLineItems(
          data.map((item) => ({
            id: item.id,
            service_item_id: item.service_item_id || undefined,
            proposal_line_item_id: item.proposal_line_item_id || undefined, // Link to proposal line item
            contract_service_id: item.contract_service_id || undefined, // Backend link to contract service
            description: item.description || '',
            quantity: item.quantity || 1,
            is_variable: item.is_variable || (item.quantity === 0), // Support legacy: quantity=0 means variable
            unit_type: item.unit_type || 'flat_rate',
            unit_price: item.unit_price || 0,
            total_price: item.total_price || 0,
            sort_order: item.sort_order || 0,
            linked_service_id: item.contract_service_id || undefined, // Frontend link for display
          }))
        )
      }
    } catch (error) {
      console.error('Error fetching contract line items:', error)
    }
  }

  // Line item management functions
  const handleLineItemChange = (index: number, field: string, value: string | number | boolean) => {
    const newLineItems = [...lineItems]
    newLineItems[index] = { ...newLineItems[index], [field]: value }

    // Recalculate total if quantity or unit_price changed (only if not variable)
    if ((field === 'quantity' || field === 'unit_price') && !newLineItems[index].is_variable) {
      newLineItems[index].total_price =
        Number(newLineItems[index].quantity) * Number(newLineItems[index].unit_price)
    } else if (field === 'is_variable') {
      // When toggling variable, reset total_price if variable, recalculate if not
      if (value) {
        newLineItems[index].total_price = 0
        newLineItems[index].quantity = 0
      } else {
        newLineItems[index].total_price =
          Number(newLineItems[index].quantity) * Number(newLineItems[index].unit_price)
      }
    }

    setLineItems(newLineItems)
  }

  const handleServiceItemSelect = (index: number, serviceItemId: string) => {
    if (!serviceItemId) {
      // Clear service item link
      const newLineItems = [...lineItems]
      newLineItems[index] = {
        ...newLineItems[index],
        service_item_id: undefined,
      }
      setLineItems(newLineItems)
      return
    }

    // Find the selected service item
    let selectedItem: { name: string; unit_type: string; base_price: number } | null = null
    for (const group of serviceItemsGrouped) {
      const item = group.items.find((i) => i.id === serviceItemId)
      if (item) {
        selectedItem = item
        break
      }
    }

    if (!selectedItem) {
      return
    }

    // Capture values to avoid TypeScript null checks in callbacks
    const itemName = selectedItem.name
    const itemBasePrice = selectedItem.base_price
    const normalizedUnitType = normalizeUnitType(selectedItem.unit_type)
    const newLineItems = [...lineItems]
    const currentQuantity = Number(newLineItems[index].quantity)

    // Check if there's a matching service with the same service_item_id
    const matchingService = contractServices.find(
      (service) => service.service_item_id === serviceItemId
    )

    if (matchingService) {
      // Show dialog asking if user wants to link
      setLinkDialog({
        show: true,
        type: 'line-item-to-service',
        lineItem: {
          ...newLineItems[index],
          service_item_id: serviceItemId,
          description: itemName,
          unit_type: normalizedUnitType,
          unit_price: itemBasePrice,
          total_price: currentQuantity * itemBasePrice,
        },
        matchingService: matchingService,
        onConfirm: () => {
          // Link the line item to the service
          const updatedLineItems = [...lineItems]
          updatedLineItems[index] = {
            ...updatedLineItems[index],
            service_item_id: serviceItemId,
            description: itemName,
            unit_type: normalizedUnitType,
            unit_price: itemBasePrice,
            total_price: currentQuantity * itemBasePrice,
            linked_service_id: matchingService.id, // Link to service
          }
          setLineItems(updatedLineItems)
          setLinkDialog(null)
        },
        onCancel: () => {
          // Still update the line item but don't link
          const updatedLineItems = [...lineItems]
          updatedLineItems[index] = {
            ...updatedLineItems[index],
            service_item_id: serviceItemId,
            description: itemName,
            unit_type: normalizedUnitType,
            unit_price: itemBasePrice,
            total_price: currentQuantity * itemBasePrice,
          }
          setLineItems(updatedLineItems)
          setLinkDialog(null)
        },
      })
    } else {
      // No matching service, just update the line item
      newLineItems[index] = {
        ...newLineItems[index],
        service_item_id: serviceItemId,
        description: itemName,
        unit_type: normalizedUnitType,
        unit_price: itemBasePrice,
        total_price: currentQuantity * itemBasePrice,
      }
      setLineItems(newLineItems)
    }
  }

  const addLineItem = () => {
    const newItem: ContractLineItem = {
      id: `line_item_${Date.now()}`,
      service_item_id: undefined,
      description: '',
      quantity: 1,
      is_variable: false,
      unit_type: 'flat_rate',
      unit_price: 0,
      total_price: 0,
      sort_order: lineItems.length,
    }
    setLineItems([...lineItems, newItem])
  }

  const removeLineItem = (index: number) => {
    const newLineItems = lineItems.filter((_, i) => i !== index)
    // Recalculate sort_order for remaining items
    const updatedLineItems = newLineItems.map((item, idx) => ({
      ...item,
      sort_order: idx,
    }))
    setLineItems(updatedLineItems)
  }

  const handlePricingModeChange = (mode: PricingMode) => {
    setFormData((prev) => ({
      ...prev,
      document_data: { ...prev.document_data, pricingMode: mode },
      billing_method:
        mode === 'rate_card' && (!prev.billing_method || prev.billing_method === 'per_period')
          ? 'actual_usage'
          : prev.billing_method,
    }))
  }

  const handleSeedRateCard = () => {
    if (!hasRateCardSeed(formData.service_type)) return
    if (
      lineItems.length > 0 &&
      !window.confirm('Replace existing line items with rate card tiers?')
    ) {
      return
    }
    setLineItems(buildRateCardLineItems(formData.service_type) as ContractLineItem[])
  }

  // Open service selection modal
  const openServiceSelectionModal = () => {
    if (contractServices.length === 0 && dsnyScheduleRows.length === 0) {
      setNotification({
        type: 'error',
        message: 'No services to import. Please add services in the Services & Schedule tab first.'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    setSelectedServicesForImport(new Set())
    setShowServiceSelectionModal(true)
  }

  // Import selected services as line items
  const importSelectedServicesAsLineItems = () => {
    if (selectedServicesForImport.size === 0) {
      setNotification({
        type: 'error',
        message: 'Please select at least one service to import.'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const newLineItems: ContractLineItem[] = []
    let sortOrder = lineItems.length

    // Import from contractServices (one-time and other recurring services)
    contractServices.forEach((service) => {
      if (selectedServicesForImport.has(service.id)) {
        newLineItems.push({
          id: `imported_${service.id}`,
          service_item_id: service.service_item_id || undefined,
          description: service.service_name || service.description || '',
          quantity: 1,
          is_variable: false,
          unit_type: 'flat_rate',
          unit_price: service.amount || 0,
          total_price: service.amount || 0,
          sort_order: sortOrder++,
          linked_service_id: service.id,
        })
      }
    })

    // For DSNY maintenance, create a single package line item
    if (recurringScheduleType === 'maintenance_dsny' && selectedServicesForImport.has('dsny_package')) {
      const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
      const defaultPackageName =
        dsnyPackageKind === 'exterior_maintenance' ? 'Exterior Maintenance' : 'Building Upkeep'

      if (!packageItem) {
        console.warn(
          `${defaultPackageName} service item not found in catalog. Available items:`,
          serviceItemsGrouped.flatMap((g) => g.items.map((i) => i.name))
        )
      }

      // Count total days across all DSNY rows
      const totalDays = dsnyScheduleRows.reduce(
        (sum, row) => sum + (Array.isArray(row.days) ? row.days.length : 0),
        0
      )

      const rawUnitType = packageItem?.unit_type || 'flat_rate'
      const unitType = normalizeUnitType(rawUnitType) // Normalize to match dropdown options
      const unitPrice = packageItem?.base_price || 0

      newLineItems.push({
        id: `dsny_package_${Date.now()}`,
        service_item_id: packageItem?.id || undefined,
        description: packageItem?.name || defaultPackageName,
        quantity: totalDays > 0 ? 1 : 0, // 1 if there are scheduled days, 0 if variable
        is_variable: totalDays === 0, // Variable if no scheduled days
        unit_type: unitType, // Use normalized unit_type from the service catalog
        unit_price: unitPrice,
        total_price: unitPrice * (totalDays > 0 ? 1 : 0),
        sort_order: sortOrder++,
        linked_service_id: 'dsny_package',
      })
    }

    if (newLineItems.length > 0) {
      setLineItems([...lineItems, ...newLineItems])
      setShowServiceSelectionModal(false)
      setSelectedServicesForImport(new Set())
      setNotification({
        type: 'success',
        message: `Added ${newLineItems.length} service(s) as line items.`
      })
      setTimeout(() => setNotification(null), 3000)
    }
  }

  const toggleServiceSelection = (serviceId: string) => {
    setSelectedServicesForImport(prev => {
      const newSet = new Set(prev)
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId)
      } else {
        newSet.add(serviceId)
      }
      return newSet
    })
  }

  // Calculate billing amounts based on method and duration
  const calculateBillingAmounts = () => {
    // For variable items, use unit_price (represents the amount per billing period)
    // For fixed items, use total_price
    const lineItemsTotal = lineItems.reduce((sum, item) => {
      if (item.is_variable) {
        return sum + (item.unit_price || 0)
      }
      return sum + (item.total_price || 0)
    }, 0)

    // Count only items with quantity > 0 (fixed price items)
    const fixedItems = lineItems.filter(item => item.quantity > 0 && !item.is_variable)
    const variableItems = lineItems.filter(item => item.is_variable)

    return {
      lineItemsTotal,
      fixedTotal: fixedItems.reduce((sum, item) => sum + (item.total_price || 0), 0),
      variableTotal: variableItems.reduce((sum, item) => sum + (item.unit_price || 0), 0),
      variableCount: variableItems.length,
      hasVariableItems: variableItems.length > 0
    }
  }

  // Calculate number of billing periods between start and end date
  const calculateBillingPeriods = () => {
    if (!formData.start_date || !formData.end_date) return 0

    // Parse dates - split the date string to avoid timezone issues
    const parseLocalDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day) // month is 0-indexed in Date constructor
    }

    const start = parseLocalDate(formData.start_date)
    const end = parseLocalDate(formData.end_date)

    // For weekly and bi-weekly, use day-based calculation
    const diffTime = end.getTime() - start.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 to include both start and end days

    switch (formData.billing_frequency) {
      case 'weekly':
        return Math.ceil(diffDays / 7)
      case 'bi_weekly':
        return Math.ceil(diffDays / 14)
      case 'monthly': {
        // Calculate actual months between dates
        const startYear = start.getFullYear()
        const startMonth = start.getMonth() // 0-11
        const endYear = end.getFullYear()
        const endMonth = end.getMonth() // 0-11

        // Calculate the number of months between dates
        // Example: Jan (0) to Dec (11) = 11 months difference
        // But we need to count billing periods: Jan, Feb, ..., Dec = 12 periods
        const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth)

        // Return the number of months (inclusive of both start and end)
        // For Jan 1 to Dec 31: monthsDiff = 11, so periods = 12 ✓
        return monthsDiff + 1
      }
      case 'quarterly': {
        // Calculate actual months and divide by 3
        const startYear = start.getFullYear()
        const startMonth = start.getMonth()
        const endYear = end.getFullYear()
        const endMonth = end.getMonth()
        const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth)
        // Count quarters: divide months by 3 and round up
        return Math.ceil((monthsDiff + 1) / 3)
      }
      case 'annually': {
        // Calculate actual years between dates
        const startYear = start.getFullYear()
        const endYear = end.getFullYear()
        const yearsDiff = endYear - startYear
        // Add 1 to include both start and end years
        return yearsDiff + 1
      }
      case 'one_time':
        return 1
      default:
        return 0
    }
  }

  const fetchVisitGroups = async (
    contractId: string,
    reconcileCtx?: { contractServices?: any[]; dsnyRows?: typeof dsnyScheduleRows }
  ) => {
    try {
      const { data, error } = await supabase
        .from('contract_visit_groups')
        .select(
          'id, name, recurrence_type, recurrence_days, recurrence_weekday, recurrence_occurrence, recurrence_day_of_month, start_date, end_date, scheduled_start_time, scheduled_end_time, dsny_integration, dsny_pickup_days, is_active, checklist_items, checklist_overrides, assigned_crew_id, assigned_employee_id, contract_visit_group_services(contract_service_id, schedule_mode, recurrence_type, recurrence_days, recurrence_weekday, recurrence_occurrence, recurrence_day_of_month, recurrence_nearest_visit)'
        )
        .eq('contract_id', contractId)
        .order('created_at', { ascending: true })
      if (error) throw error
      const normalized: ContractVisitGroup[] = (data || []).map((g: any) =>
        normalizeVisitGroupFromDb(g, g.contract_visit_group_services || [], contractId)
      )
      const cs = reconcileCtx?.contractServices ?? contractServicesRef.current
      const rows = reconcileCtx?.dsnyRows ?? dsnyScheduleRowsRef.current
      const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
      const reconciled = preserveVisitGroupTargetAssignees(
        normalized,
        ensureDsnyVisitGroupsForDisplay(normalized, cs, {
          scheduleRows: rows as any,
          packageLabel:
            packageItem?.name ||
            (dsnyPackageKind === 'exterior_maintenance'
              ? 'Exterior Maintenance'
              : 'Building Upkeep'),
          contractId,
          contractStartDate: formData.start_date || null,
          contractEndDate: formData.end_date || null,
        })
      ) as ContractVisitGroup[]
      // Other-recurring imports sometimes save visit groups with employees but empty
      // days; backfill from linked service cadence so the board matches the schedule.
      const boardServices = (cs || []).map((s: any) => ({
        id: s.id,
        recurrence_days: s.schedule?.recurrence_days || s.recurrence_days || [],
      }))
      setVisitGroups(fillEmptyVisitGroupDaysFromServices(reconciled, boardServices))
      if (rows.length > 0) {
        const hydratedRows = rows.map((row) => {
          const serviceId = row.persistedServiceId || `dsny_${row.id}`
          const altTempId = `dsny_${row.id}`
          const combineTarget = resolveDsnyCombineTarget(row)
          let group = reconciled.find((candidate) =>
            getGroupAssignments(candidate).some(
              (assignment) =>
                assignment.service_id === serviceId ||
                assignment.service_id === altTempId
            )
          )
          if (!group && combineTarget === 'dsny') {
            group = reconciled.find((candidate) => candidate.dsny_integration)
          } else if (!group && combineTarget === 'hallway') {
            group = reconciled.find((candidate) =>
              /^Hallway Cleaning\b/i.test(candidate.name || '')
            )
          }
          if (!group) {
            return {
              ...row,
              combine_target: combineTarget,
              combine_with_dsny: combineTarget === 'dsny',
            }
          }
          const fromHost: DsnyCombineTarget = group.dsny_integration
            ? 'dsny'
            : /^Hallway Cleaning\b/i.test(group.name || '')
              ? 'hallway'
              : null
          // Prefer an explicit persisted visit target (column/schedule) over host
          // inference so orphan "Drains (Monthly)" groups cannot wipe hallway/DSNY.
          const rowCombineTarget: DsnyCombineTarget =
            combineTarget === 'dsny' || combineTarget === 'hallway'
              ? combineTarget
              : fromHost ?? combineTarget ?? null
          return {
            ...row,
            combine_target: rowCombineTarget,
            combine_with_dsny: rowCombineTarget === 'dsny',
            // Prefer group assignees; keep any in-memory/schedule values if group is blank.
            assigned_crew_id: group.assigned_crew_id || row.assigned_crew_id || null,
            assigned_employee_id:
              group.assigned_employee_id || row.assigned_employee_id || null,
          }
        })
        dsnyScheduleRowsRef.current = hydratedRows
        setDsnyScheduleRows(hydratedRows)
      }
      setVisitGroupsLoaded(true)
      // Schedule-linked (including imports): do not treat existing groups as manual
      // customization. Manual board/editor edits still set visitGroupsCustomized=true.
      setVisitGroupsCustomized(false)
    } catch (err) {
      console.warn('Could not fetch visit groups:', err)
      setVisitGroups([])
      setVisitGroupsLoaded(true)
    }
  }

  // After services + schedule rows load, expand persisted package assignments into
  // per-row chips (Garbage, Recycling, …) and repair empty DSNY day slots.
  // Depends on schedule rows / package kind (not full contractServices) so day toggles
  // on Building Upkeep or Exterior do not double-reconcile after package sync.
  useEffect(() => {
    if (recurringScheduleType !== 'maintenance_dsny') return
    if (!visitGroupsLoaded || contractServicesRef.current.length === 0) return

    setVisitGroups((prev) => {
      if (visitGroupsCustomized) return prev
      if (prev.length === 0) return prev
      const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
      const fixed = preserveVisitGroupTargetAssignees(
        prev,
        ensureDsnyVisitGroupsForDisplay(prev, contractServicesRef.current, {
          scheduleRows: dsnyScheduleRows as any,
          packageLabel:
            packageItem?.name ||
            (dsnyPackageKind === 'exterior_maintenance'
              ? 'Exterior Maintenance'
              : 'Building Upkeep'),
          contractId: id || '',
          contractStartDate: formData.start_date || null,
          contractEndDate: formData.end_date || null,
        })
      )
      return JSON.stringify(fixed) === JSON.stringify(prev) ? prev : fixed
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visitGroupsLoaded,
    visitGroupsCustomized,
    contractServices.length,
    dsnyScheduleRows,
    dsnyPackageKind,
    recurringScheduleType,
    serviceItemsGrouped,
    id,
    formData.start_date,
    formData.end_date,
  ])

  // Build a list of the DSNY schedule rows in the shape the auto-attach logic / board
  // understands. Non-weekly custom/hallway rows become services keyed by persisted
  // uuid or `dsny_<rowId>` (same ids deriveDsnyContractArtifacts produces).
  const getEffectiveDsnyRowServices = () => {
    const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
    return buildDsnyVisitBoardServices(
      dsnyScheduleRows as any,
      contractServices,
      packageItem?.name ||
        (dsnyPackageKind === 'exterior_maintenance'
          ? 'Exterior Maintenance'
          : 'Building Upkeep')
    )
  }

  const getEffectiveServicesForVisitGroups = () => {
    const catalogById = new Map<string, any>()
    const catalogByName = new Map<string, any>()
    for (const group of serviceItemsGrouped) {
      for (const item of group.items || []) {
        catalogById.set(item.id, item)
        catalogByName.set(String(item.name || '').trim().toLowerCase(), item)
      }
    }
    const attachWork = (s: any) => {
      const fromId = s.service_item_id ? catalogById.get(s.service_item_id) : null
      const fromName = catalogByName.get(String(s.service_name || s.name || '').trim().toLowerCase())
      const catalog = fromId || fromName
      return {
        ...s,
        service_item_id: s.service_item_id || catalog?.id,
        work_description: catalog?.work_description || null,
        default_checklist: Array.isArray(catalog?.default_checklist)
          ? catalog.default_checklist
          : [],
        is_seasonal: catalog?.is_seasonal,
        season_start_month: catalog?.season_start_month ?? null,
        season_end_month: catalog?.season_end_month ?? null,
      }
    }

    if (recurringScheduleType === 'maintenance_dsny') {
      const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
      return buildDsnyVisitBoardServices(
        dsnyScheduleRows as any,
        contractServices,
        packageItem?.name ||
          (dsnyPackageKind === 'exterior_maintenance'
            ? 'Exterior Maintenance'
            : 'Building Upkeep')
      ).map(attachWork)
    }
    return contractServices.map((s: any) =>
      attachWork({
        id: s.id,
        service_name: s.service_name,
        recurrence_type: s.schedule?.recurrence_type || s.recurrence_type || 'weekly',
        recurrence_days: s.schedule?.recurrence_days || s.recurrence_days || [],
        recurrence_day_of_month:
          s.schedule?.recurrence_day_of_month ?? s.recurrence_day_of_month ?? null,
        recurrence_weekday: s.schedule?.recurrence_weekday ?? s.recurrence_weekday ?? null,
        recurrence_occurrence:
          s.schedule?.recurrence_occurrence ?? s.recurrence_occurrence ?? null,
        service_type_id: s.service_type || '',
        service_item_id: s.service_item_id,
        dsny_integration: !!(s.schedule?.dsny_integration || s.dsny_integration),
      })
    )
  }

  const getServicesForVisitGroupSeed = () => getEffectiveServicesForVisitGroups()

  const seedVisitGroupsFromServices = () => {
    const services = getServicesForVisitGroupSeed()
    if (services.length === 0) {
      setVisitGroups([])
      return
    }
    const servicesWithCategory = services.map((s: any) => ({
      ...s,
      service_type_id:
        s.service_type_id ||
        s.category_id ||
        (s.combine_target === 'dsny'
          ? maintenanceDSNYCategoryId
          : s.combine_target === 'hallway'
            ? 'hallway_cleaning'
            : `standalone_${s.id}`),
    }))
    const seeded =
      recurringScheduleType === 'maintenance_dsny'
        ? finalizeDsnyVisitGroupsAfterSeed(
            suggestGroupsDayCentric(
              servicesWithCategory,
              id || '',
              formData.start_date || null,
              formData.end_date || null
            ),
            findDsnyPackageBoardId(contractServices),
            getEffectiveServicesForVisitGroups() as any,
            dsnyScheduleRows as any,
            contractServices
          )
        : suggestGroupsDayCentric(
            servicesWithCategory,
            id || '',
            formData.start_date || null,
            formData.end_date || null
          )
    setVisitGroups(fillEmptyVisitGroupDaysFromServices(seeded, services as any))
  }

  const handleVisitGroupsManualChange = (groups: ContractVisitGroup[]) => {
    setVisitGroups(groups)
    setVisitGroupsCustomized(true)
  }

  /** Persist visit groups + assignees without a full contract submit. */
  const saveVisitGroups = async () => {
    if (!id) return
    try {
      setSavingVisitGroups(true)
      const boardServices = getEffectiveServicesForVisitGroups() as any
      const filled = fillEmptyVisitGroupDaysFromServices(visitGroups, boardServices)
      const groupsToPersist =
        recurringScheduleType === 'maintenance_dsny'
          ? ensureDsnyVisitGroupsForDisplay(
              syncVisitGroupAssigneesFromScheduleRows(filled, dsnyScheduleRows as any),
              contractServicesRef.current,
              {
                scheduleRows: dsnyScheduleRows as any,
                contractId: id,
                contractStartDate: formData.start_date || null,
                contractEndDate: formData.end_date || null,
              }
            )
          : filled
      await replaceContractVisitGroups(id, groupsToPersist, undefined, { strict: false })
      setVisitGroupsCustomized(true)
      await fetchVisitGroups(id)
      setNotification({ message: 'Visit groups saved successfully.', type: 'success' })
      setTimeout(() => setNotification(null), 4000)
    } catch (error) {
      console.error('Error saving visit groups:', error)
      setNotification({
        message: 'Failed to save visit groups: ' + (error as any).message,
        type: 'error',
      })
      setTimeout(() => setNotification(null), 6000)
    } finally {
      setSavingVisitGroups(false)
    }
  }

  const handleReDeriveVisitGroups = () => {
    if (
      visitGroupsCustomized &&
      !window.confirm(
        'Re-deriving will rebuild crew visits from the schedule and discard your manual changes. Continue?'
      )
    ) {
      return
    }
    const priorGroups = visitGroups
    const priorRows = dsnyScheduleRows
    seedVisitGroupsFromServices()
    // Keep Exterior/Interior assignees across structure rebuild.
    setVisitGroups((seeded) => preserveVisitGroupTargetAssignees(priorGroups, seeded))
    setDsnyScheduleRows((rows) => {
      const exterior =
        priorRows.find(
          (r) =>
            resolveDsnyCombineTarget(r as any) === 'dsny' &&
            (r.assigned_crew_id || r.assigned_employee_id)
        ) || priorGroups.find((g) => g.dsny_integration && (g.assigned_crew_id || g.assigned_employee_id))
      const interior =
        priorRows.find(
          (r) =>
            resolveDsnyCombineTarget(r as any) === 'hallway' &&
            (r.assigned_crew_id || r.assigned_employee_id)
        ) ||
        priorGroups.find(
          (g) =>
            /^Hallway Cleaning\b/i.test(g.name || '') &&
            (g.assigned_crew_id || g.assigned_employee_id)
        )
      return rows.map((row) => {
        const target = resolveDsnyCombineTarget(row as any)
        if (target === 'dsny' && exterior) {
          return {
            ...row,
            assigned_crew_id:
              (exterior as any).assigned_crew_id || row.assigned_crew_id || null,
            assigned_employee_id:
              (exterior as any).assigned_employee_id || row.assigned_employee_id || null,
          }
        }
        if (target === 'hallway' && interior) {
          return {
            ...row,
            assigned_crew_id:
              (interior as any).assigned_crew_id || row.assigned_crew_id || null,
            assigned_employee_id:
              (interior as any).assigned_employee_id || row.assigned_employee_id || null,
          }
        }
        return row
      })
    })
    setVisitGroupsCustomized(false)
  }

  // Keep non-weekly rows represented on the board when editing. Piggyback-combine
  // rows (monthly hallway/DSNY) attach to the weekly host — never as an isolated
  // "Drains (Monthly)" group with no days.
  useEffect(() => {
    if (recurringScheduleType !== 'maintenance_dsny') return
    if (!visitGroupsLoaded) return
    const services = getEffectiveDsnyRowServices() as any[]
    if (services.length === 0) return

    setVisitGroups((prev) => {
      const next = attachMissingNonWeeklyBoardServices(prev, services, {
        contractId: id || '',
        contractStartDate: formData.start_date || null,
        contractEndDate: formData.end_date || null,
      })
      return next === prev ? prev : next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dsnyScheduleRows,
    recurringScheduleType,
    visitGroupsLoaded,
    formData.start_date,
    formData.end_date,
  ])

  // Keep Exterior/Interior visit-group dropdowns in sync with schedule assignees.
  useEffect(() => {
    if (recurringScheduleType !== 'maintenance_dsny') return
    if (!visitGroupsLoaded) return
    setVisitGroups((prev) => {
      const next = syncVisitGroupAssigneesFromScheduleRows(prev, dsnyScheduleRows as any)
      const same =
        next.length === prev.length &&
        next.every(
          (g, i) =>
            g.assigned_crew_id === prev[i]?.assigned_crew_id &&
            g.assigned_employee_id === prev[i]?.assigned_employee_id
        )
      return same ? prev : next
    })
  }, [dsnyScheduleRows, recurringScheduleType, visitGroupsLoaded])

  const fetchContract = async (contractId: string) => {
    try {
      setLoading(true)

      const { data: contractData, error: contractError } = await supabase
        .from('contracts')
        .select(
          `
          *,
          customer:customers(id, company_name, contact_first_name, contact_last_name),
          property:properties(id, name, address, city, state)
        `
        )
        .eq('id', contractId)
        .single()

      if (contractError) throw contractError

      setContract(contractData)
      const savedScope = ((contractData as any).scope_type ||
        'single_property') as ContractScopeType
      const propertyLinks = await fetchContractProperties(contractId)
      const activePropertyIds = propertyLinks
        .filter((link) => link.status === 'active')
        .map((link) => link.property_id)
      setScopeType(savedScope)
      setSelectedPropertyIds(
        activePropertyIds.length > 0
          ? activePropertyIds
          : contractData.property_id
            ? [contractData.property_id]
            : []
      )

      // Services must load before visit groups so package uuids expand to row chips.
      const isDsnyContract =
        !!(contractData as any).dsny_integration ||
        isDsnyServiceType(contractData.service_type)
      const { services, dsnyRows } = await fetchContractServices(contractId, {
        contractType: contractData.contract_type,
        isDsnyContract,
      })
      await fetchVisitGroups(contractId, { contractServices: services, dsnyRows })

      // Fetch contract line items
      await fetchContractLineItems(contractId)

      // If contract has a proposal, fetch proposal line items for linking
      if (contractData.proposal_id) {
        try {
          const { data: proposalLineItemsData } = await supabase
            .from('proposal_line_items')
            .select('*')
            .eq('proposal_id', contractData.proposal_id)

          if (proposalLineItemsData) {
            setProposalLineItems(proposalLineItemsData)
            console.log('📋 Loaded proposal line items for linking:', proposalLineItemsData)
          }
        } catch (error) {
          console.warn('Could not fetch proposal line items:', error)
        }
      }

      setFormData({
        title: contractData.title || '',
        description: contractData.description || '',
        template_type: contractData.template_type || '',
        contract_type: contractData.contract_type || 'one_time',
        service_type: contractData.service_type || '',
        customer_id: contractData.customer_id || '',
        property_id: contractData.property_id || '',
        proposal_id: contractData.proposal_id || '',
        parent_contract_id: contractData.parent_contract_id || '',
        original_contract_id: contractData.original_contract_id || '',
        total_amount: contractData.total_amount || 0,
        contract_total_amount: contractData.contract_total_amount || 0,
        billing_frequency: contractData.billing_frequency || 'monthly',
        billing_day_of_month: Number((contractData as any).billing_day_of_month) || 1,
        billing_method: contractData.billing_method || 'per_period',
        is_recurring: contractData.is_recurring || false,
        recurrence_type: contractData.recurrence_type || 'weekly',
        recurrence_interval: contractData.recurrence_interval || 1,
        recurrence_days: contractData.recurrence_days || [],
        recurrence_end_date: contractData.recurrence_end_date || '',
        recurrence_end_count: contractData.recurrence_end_count || 0,
        status: contractData.status || 'draft',
        start_date: contractData.start_date || '',
        end_date: contractData.end_date || '',
        cancel_date: contractData.cancel_date || '',
        payment_terms: contractData.payment_terms || 'Net 30',
        late_fee_percentage: contractData.late_fee_percentage || 0,
        sales_tax_status: contractData.sales_tax_status || '',
        cancellation_terms: contractData.cancellation_terms || '',
        notes: '', // Notes are now handled separately as an array
        // Comprehensive Schedule System
        master_weekly_schedule: contractData.master_weekly_schedule || [],
        garbage_schedule: contractData.garbage_schedule || [],
        recycling_schedule: contractData.recycling_schedule || [],
        bulk_schedule: contractData.bulk_schedule || [],
        organics_schedule: contractData.organics_schedule || [],
        interior_cleaning_schedule: contractData.interior_cleaning_schedule || [],
        // Dynamic manual schedules
        manual_schedules: contractData.manual_schedules || [],
        dsny_integration: contractData.dsny_integration || false,
        // Legacy fields for compatibility
        dsny_pickup_days: contractData.dsny_pickup_days || [],
        dsny_collection_types: contractData.dsny_collection_types || [],
        payment_method: contractData.payment_method || '',
        scope_of_work: contractData.scope_of_work || '',
        document_data:
          contractData.document_data && Object.keys(contractData.document_data).length > 0
            ? contractData.document_data
            : buildDefaultDocumentData(contractData.service_type),
        assignment_source:
          (contractData.assignment_source as AssignmentSource) || 'property_default',
        default_assigned_crew_id: contractData.default_assigned_crew_id || '',
        default_assigned_employee_id: contractData.default_assigned_employee_id || '',
      })

      // Pre-select the "months" duration mode when the saved end date is exactly
      // start + N months - 1 day; otherwise keep the manual "end date" mode.
      const inferredMonths = inferDurationMonths(
        contractData.start_date || '',
        contractData.end_date || ''
      )
      if (inferredMonths) {
        setDurationMonths(inferredMonths)
        setDurationMode('months')
      } else {
        setDurationMode('end_date')
      }

      try {
        const milestones = await fetchContractMilestones(contractData.id)
        setBillingMilestones(milestones)
      } catch {
        setBillingMilestones([])
      }

      // Fetch notes - handle both JSONB array and JSON string
      let parsedNotes: Array<{ timestamp: string; note: string }> = []
      if (contractData.notes) {
        if (Array.isArray(contractData.notes)) {
          parsedNotes = contractData.notes
        } else if (typeof contractData.notes === 'string') {
          try {
            const parsed = JSON.parse(contractData.notes)
            if (Array.isArray(parsed)) {
              parsedNotes = parsed
            }
          } catch (e) {
            console.warn('Failed to parse notes as JSON:', e)
            // If it's a plain string, convert to array format
            if (contractData.notes.trim()) {
              parsedNotes = [{
                timestamp: new Date().toISOString(),
                note: contractData.notes
              }]
            }
          }
        }
      }
      setNotes(parsedNotes)
      console.log('Fetched notes:', parsedNotes)

      if (contractData.contract_type === 'recurring') {
        const hasDsnyServices = services.some(
          (s: any) => s.dsny_integration || s.schedule?.dsny_integration
        )
        setRecurringScheduleType(
          isDsnyContract || hasDsnyServices ? 'maintenance_dsny' : 'other'
        )
      }
    } catch (error) {
      console.error('Error fetching contract:', error)
      alert('Error loading contract details')
    } finally {
      setLoading(false)
    }
  }

  // DSNY Integration Functions
  const isMaintenanceService = () => {
    const selectedService = serviceCategories.find(
      (service) => service.id === formData.service_type
    )
    return selectedService?.operational_division_id === 'aad2c279-31a9-4f3d-bc5e-195f218b38d7' // Maintenance division ID
  }

  const fetchDSNYPickupSchedule = async (address: string) => {
    const result = await getDSNYPickupDays(address)
    if (!result) {
      throw new Error(
        'Could not fetch DSNY schedule. Please verify the property address is a valid NYC address and try again.'
      )
    }
    return result
  }

  const handleDSNYFetch = async () => {
    if (!formData.property_id) {
      alert('Please select a property first')
      return
    }

    const selectedProperty = properties.find((p) => p.id === formData.property_id)
    if (!selectedProperty) {
      alert('Property not found')
      return
    }

    try {
      const dsnyData = await fetchDSNYPickupSchedule(selectedProperty.address)

      // Extract individual collection schedules
      const garbageDays = dsnyData.schedules.garbage || []
      const recyclingDays = dsnyData.schedules.recycling || []
      const organicsDays = dsnyData.schedules.organics || []
      const bulkDays = dsnyData.schedules.bulk || []
      const dsnyPickupDayNames = pickupSchedulesToPickupDayNames(dsnyData.pickupSchedules)

      const allDSNYDays = Array.from(
        new Set([...garbageDays, ...recyclingDays, ...organicsDays, ...bulkDays])
      )

      setFormData((prev) => ({
        ...prev,
        dsny_integration: true,
        garbage_schedule: garbageDays,
        recycling_schedule: recyclingDays,
        organics_schedule: organicsDays,
        bulk_schedule: bulkDays,
        master_weekly_schedule: allDSNYDays,
        recurrence_days: allDSNYDays,
        recurrence_type: 'custom',
        dsny_pickup_days: dsnyPickupDayNames,
        dsny_collection_types:
          prev.dsny_collection_types.length > 0
            ? prev.dsny_collection_types
            : ['garbage', 'recycling'],
      }))

      alert(
        `DSNY schedule loaded for ${selectedProperty.address}!\nZone: ${dsnyData.zone}\nService days set to the evening before each DSNY pickup.`
      )
    } catch (error) {
      console.error('Error fetching DSNY schedule:', error)
      alert('Error fetching DSNY schedule')
    }
  }

  // Manual Schedule Functions
  const addManualSchedule = () => {
    const newSchedule = {
      id: `manual_${Date.now()}`,
      name: '',
      description: '',
      color: 'bg-indigo-100 border-indigo-300 text-indigo-800',
      days: [] as number[],
    }
    setFormData((prev) => ({
      ...prev,
      manual_schedules: [...prev.manual_schedules, newSchedule],
    }))
  }

  const updateManualSchedule = (id: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      manual_schedules: prev.manual_schedules.map((schedule) =>
        schedule.id === id ? { ...schedule, [field]: value } : schedule
      ),
    }))
  }

  const removeManualSchedule = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      manual_schedules: prev.manual_schedules.filter((schedule) => schedule.id !== id),
    }))
  }

  const updateManualScheduleDays = (id: string, day: number, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      manual_schedules: prev.manual_schedules.map((schedule) =>
        schedule.id === id
          ? {
              ...schedule,
              days: checked ? [...schedule.days, day] : schedule.days.filter((d) => d !== day),
            }
          : schedule
      ),
    }))
  }

  const fetchActivities = async () => {
    if (!id) return
    try {
      const acts = await getEntityActivities('contract', id)
      setActivities(acts)

      // Fetch related items
      const { data: jobsData } = await supabase.from('jobs').select('*').eq('contract_id', id)

      const { data: invoicesData } = await supabase
        .from('invoices')
        .select('*')
        .eq('contract_id', id)

      const { data: proposalsData } = await supabase
        .from('proposals')
        .select('*')
        .eq('contract_id', id)

      setRelatedItems({
        jobs: jobsData || [],
        invoices: invoicesData || [],
        proposals: proposalsData || [],
      })
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  const addNote = async () => {
    if (!newNote.trim() || !id) return

    setSavingNote(true)
    try {
      const noteToAdd: { timestamp: string; note: string } = {
        timestamp: new Date().toISOString(),
        note: newNote.trim(),
      }

      const updatedNotes = [noteToAdd, ...notes]

      console.log('Saving notes:', updatedNotes)

      const { error } = await supabase
        .from('contracts')
        .update({ notes: updatedNotes }) // Supabase should handle JSONB arrays automatically
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)
      setNewNote('')

      // Log activity
      await logActivity({
        activity_type: 'note_added',
        entity_type: 'contract',
        entity_id: id!,
        description: 'Note added to contract',
      })
    } catch (error) {
      console.error('Error adding note:', error)
      alert('Error adding note')
    } finally {
      setSavingNote(false)
    }
  }

  const removeNote = async (index: number) => {
    if (!id) return

    setSavingNote(true)
    try {
      const updatedNotes = notes.filter((_, i) => i !== index)

      const { error } = await supabase
        .from('contracts')
        .update({ notes: updatedNotes.length > 0 ? updatedNotes : null })
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)

      // Log activity
      await logActivity({
        activity_type: 'note_removed',
        entity_type: 'contract',
        entity_id: id!,
        description: 'Note removed from contract',
      })
    } catch (error) {
      console.error('Error removing note:', error)
      alert('Error removing note')
    } finally {
      setSavingNote(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'Enter') {
      addNote()
    }
  }

  // Status dropdown: persisted status + allowed workflow transitions for this contract type.
  const statusDropdownOptions = React.useMemo(() => {
    const workflowType = contractWorkflowType({
      is_recurring: formData.is_recurring,
      contract_type: formData.contract_type,
    })
    const persisted = contract?.status || formData.status || 'draft'
    const allowed = new Set([persisted, ...getAllowedContractTransitions(workflowType, persisted)])
    // Keep the currently selected value visible even if it isn't a valid transition.
    allowed.add(formData.status)
    const known = CONTRACT_STATUS_OPTIONS[workflowType].filter((o) => allowed.has(o.value))
    const knownValues = new Set(known.map((o) => o.value))
    const extras = Array.from(allowed)
      .filter((v) => v && !knownValues.has(v))
      .map((v) => ({ value: v, label: formatContractStatusLabel(v) }))
    return [...known, ...extras]
  }, [contract?.status, formData.status, formData.is_recurring, formData.contract_type])

  // Build the agreement state used by the consistency rules engine
  const agreementState: AgreementState = React.useMemo(() => {
    const selectedProp = (properties || []).find((p: any) => p.id === formData.property_id)
    return {
      contract_type: (formData.contract_type as any) || '',
      service_type: formData.service_type as any,
      billing_frequency: formData.billing_frequency as any,
      billing_method: (formData as any).billing_method as any,
      payment_method: (formData as any).payment_method as any,
      template_type: formData.template_type as any,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      property_address: selectedProp?.address || '',
      line_items: (lineItems || []).map((li: any) => {
        // Building Upkeep packages often have null recurrence on the service row;
        // schedule lives on visit groups / DSNY days. Resolve both so B1 does not block save.
        const resolved = resolveLineItemRecurrenceForConsistency(li, contractServices, {
          garbage_schedule: formData.garbage_schedule,
          recycling_schedule: formData.recycling_schedule,
          organics_schedule: formData.organics_schedule,
          bulk_schedule: formData.bulk_schedule,
          visit_groups: visitGroups || [],
        })
        return {
          id: li.id,
          service_item_id: li.service_item_id,
          description: li.description,
          quantity: li.quantity,
          is_variable: li.is_variable,
          unit_type: li.unit_type,
          unit_price: li.unit_price,
          total_price: li.total_price,
          sort_order: li.sort_order,
          recurrence_type: resolved.recurrence_type as any,
          recurrence_days: resolved.recurrence_days,
        }
      }),
      dsny_integration: formData.dsny_integration,
      garbage_schedule: formData.garbage_schedule,
      recycling_schedule: formData.recycling_schedule,
      organics_schedule: formData.organics_schedule,
      bulk_schedule: formData.bulk_schedule,
      visit_groups: (visitGroups || []).map((vg) => ({
        id: vg.id,
        name: vg.name,
        start_date: vg.start_date,
        end_date: vg.end_date,
        recurrence_type: vg.recurrence_type,
        recurrence_days: vg.recurrence_days,
      })),
      billing_milestones: billingMilestones,
      pricing_mode: formData.document_data?.pricingMode,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, lineItems, properties, visitGroups, billingMilestones, contractServices])

  const consistencyIssues = React.useMemo(
    () => checkConsistency(agreementState, 'contract'),
    [agreementState]
  )

  const handleNavigateIssue = (target: IssueTarget) => {
    if (!target) return
    ensureScrollFlashCSS()
    let local: 'overview' | 'services' | 'billing' | 'notes' = activeTab
    if (target.tab === 'overview') local = 'overview'
    else if (target.tab === 'services') local = 'services'
    else if (target.tab === 'visit_groups') {
      // Visit groups now live inside the Services & Schedule tab
      local = 'services'
      if (formData.contract_type === 'recurring') setShowVisitBoard(true)
    } else if (target.tab === 'billing') local = 'billing'
    else if (target.tab === 'notes') local = 'notes'
    if (local !== activeTab) setActiveTab(local)

    // Recurrence day pickers live on the service edit modal (Other path), not on line items.
    if (
      local === 'services' &&
      formData.contract_type === 'recurring' &&
      recurringScheduleType !== 'maintenance_dsny' &&
      (target.field === 'recurrence_days' || target.field === 'recurrence_type')
    ) {
      setRecurringScheduleType((prev) => prev || 'other')
      const incomplete = contractServices.find((s) => serviceNeedsRecurringSchedule(s))
      const linkedId =
        target.anchor && String(target.anchor).startsWith('line-item-')
          ? lineItems.find((li) => `line-item-${li.id}` === target.anchor)?.linked_service_id ||
            lineItems.find((li) => `line-item-${li.id}` === target.anchor)?.contract_service_id
          : undefined
      const targetService =
        (linkedId && contractServices.find((s) => s.id === linkedId)) ||
        incomplete ||
        contractServices.find((s) =>
          String(s.service_name || '')
            .toLowerCase()
            .includes('building upkeep')
        ) ||
        contractServices[0]
      if (targetService) {
        window.setTimeout(() => handleEditService(targetService.id), 120)
      }
    }

    if (target.anchor) {
      scrollAndFlash(target.anchor, 'block')
      // Fallback when line-item anchors are not rendered on the services tab
      window.setTimeout(() => scrollAndFlash('other-schedule', 'block'), 100)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.assignment_source) {
      alert('Choose a Job Assignment source before saving.')
      setActiveTab('services')
      return
    }

    if (
      recurringScheduleType === 'maintenance_dsny' &&
      dsnyScheduleRows.some(
        (row) =>
          (row.type === 'custom' || row.type === 'hallway_cleaning') &&
          dsnyRowNeedsCombineTarget(row)
      )
    ) {
      alert('Choose a Visit target for every schedule line before saving.')
      setActiveTab('services')
      return
    }

    // Phase 7: enforce blockers
    const { blockers } = splitIssues(consistencyIssues)
if (blockers.length > 0) {
      const first = blockers[0]
      alert(
        `Please fix ${blockers.length} issue${blockers.length === 1 ? '' : 's'} before saving.\n\n` +
        blockers.map((b) => `• ${b.message}`).join('\n')
      )
      handleNavigateIssue(first.target)
      return
    }

    // GATE: blocks promoting a recurring contract to 'active' without valid visit groups.
    // Only fires when the status is changing INTO 'active' from something else.
    const becomingActive =
      formData.status === 'active' &&
      contract?.status !== 'active' &&
      !!formData.is_recurring &&
      !!id
    if (becomingActive) {
      try {
        const { data: groups, error: groupsErr } = await supabase
          .from('contract_visit_groups')
          .select(
            'id, name, recurrence_type, recurrence_days, is_active, contract_visit_group_services(contract_service_id)'
          )
          .eq('contract_id', id)
        if (groupsErr) throw groupsErr

        const normalized: ContractVisitGroup[] = (groups || []).map((g: any) => ({
          id: g.id,
          contract_id: id!,
          name: g.name,
          recurrence_type: g.recurrence_type,
          recurrence_days: g.recurrence_days || [],
          is_active: g.is_active !== false,
          service_ids: (g.contract_visit_group_services || []).map(
            (m: any) => m.contract_service_id
          ),
        }))

        const errors = validateVisitGroups(normalized)
        if (errors.length > 0) {
          const proceed = window.confirm(
            [
              'This recurring contract cannot be activated yet because the visit groups are not fully configured:',
              '',
              ...errors.map((er) => `  • ${er}`),
              '',
              'Click OK to save as a draft instead. To activate, open the contract details and configure visit groups, or use the Mark Signed action from the contracts list.',
            ].join('\n')
          )
          if (!proceed) return
          // Downgrade to draft for this save
          setFormData((prev) => ({ ...prev, status: 'draft' }))
          ;(formData as any).status = 'draft'
        }
      } catch (err) {
        console.error('Visit-group gate check failed:', err)
        // Fall through and let the user save anyway; surface a warning.
        if (
          !window.confirm(
            'Could not verify visit groups for activation. Continue saving anyway?'
          )
        ) {
          return
        }
      }
    }

    setSaving(true)

    try {
      const { error } = await supabase
        .from('contracts')
        .update({
          title: formData.title,
          description: formData.description || null,
          template_type: formData.template_type || null,
          contract_type: formData.contract_type, // This is now one_time or recurring
          service_type: formData.service_type || null,
          customer_id: formData.customer_id || null,
          property_id: selectedPropertyIds[0] || formData.property_id || null,
          scope_type: scopeType,
          proposal_id: formData.proposal_id || null,
          total_amount: formData.total_amount || null,
          contract_total_amount: formData.contract_total_amount || null,
          billing_frequency: formData.billing_frequency || null,
          billing_day_of_month: Number((formData as any).billing_day_of_month) || 1,
          billing_method:
            formData.billing_frequency === 'progress'
              ? 'progress'
              : formData.billing_method || null,
          payment_method: (formData as any).payment_method || null,
          sales_tax_status: formData.sales_tax_status || null,
          is_recurring: formData.is_recurring,
          recurrence_type: formData.recurrence_type || null,
          recurrence_interval: formData.recurrence_interval || null,
          recurrence_days: formData.recurrence_days || null,
          recurrence_end_date: formData.recurrence_end_date || null,
          recurrence_end_count: formData.recurrence_end_count || null,
          status: formData.status,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          parent_contract_id: formData.parent_contract_id || null,
          original_contract_id: formData.original_contract_id || null,
          renewal_date: formData.parent_contract_id ? new Date().toISOString().split('T')[0] : null,
          cancel_date: formData.status === 'cancelled' && formData.cancel_date ? formData.cancel_date : null,
          payment_terms: formData.payment_terms || null,
          late_fee_percentage: formData.late_fee_percentage || null,
          cancellation_terms: formData.cancellation_terms || null,
          notes: formData.notes || null,
          master_weekly_schedule: formData.master_weekly_schedule || [],
          garbage_schedule: formData.garbage_schedule || [],
          recycling_schedule: formData.recycling_schedule || [],
          bulk_schedule: formData.bulk_schedule || [],
          organics_schedule: formData.organics_schedule || [],
          interior_cleaning_schedule: formData.interior_cleaning_schedule || [],
          manual_schedules: formData.manual_schedules || [],
          dsny_integration: formData.dsny_integration,
          dsny_pickup_days: formData.dsny_pickup_days || null,
          dsny_collection_types: formData.dsny_collection_types || null,
          scope_of_work: formData.scope_of_work || null,
          document_data: formData.document_data || {},
          assignment_source: formData.assignment_source,
          default_assigned_crew_id:
            formData.assignment_source === 'custom'
              ? formData.default_assigned_crew_id || null
              : null,
          default_assigned_employee_id:
            formData.assignment_source === 'custom'
              ? formData.default_assigned_employee_id || null
              : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)

      if (error) throw error

      if (id) {
        await syncContractProperties(
          id,
          selectedPropertyIds.length > 0
            ? selectedPropertyIds
            : formData.property_id
              ? [formData.property_id]
              : [],
          selectedPropertyIds[0] || formData.property_id || null
        )
      }

      try {
        if (id) await reapplyAssignmentDefaultsForContract(id)
      } catch (reapplyErr) {
        console.warn('Contract saved but job assignment reapply failed:', reapplyErr)
      }

      if (id && formData.billing_frequency === 'progress') {
        const contractTotal =
          Number(formData.contract_total_amount) || Number(formData.total_amount) || 0
        await saveContractMilestones(id, billingMilestones, contractTotal)
      }

      // Handle contract cancellation - delete future jobs and templates
      if (formData.status === 'cancelled' && formData.cancel_date && id) {
        try {
          // Delete jobs scheduled after cancel_date
          const { error: jobsDeleteError } = await supabase
            .from('jobs')
            .delete()
            .eq('contract_id', id)
            .gt('scheduled_date', formData.cancel_date)

          if (jobsDeleteError) {
            console.error('Error deleting future jobs:', jobsDeleteError)
          } else {
            console.log('✅ Deleted future jobs after cancel_date:', formData.cancel_date)
          }

          // Delete templates scheduled after cancel_date
          const { error: templatesDeleteError } = await supabase
            .from('contract_job_templates')
            .delete()
            .eq('contract_id', id)
            .gt('scheduled_date', formData.cancel_date)

          if (templatesDeleteError) {
            console.error('Error deleting future templates:', templatesDeleteError)
          } else {
            console.log('✅ Deleted future templates after cancel_date:', formData.cancel_date)
          }
        } catch (cancelError) {
          console.error('Error handling contract cancellation:', cancelError)
          // Don't fail the whole operation, just log the error
        }
      }

      // Update contract services - convert DSNY schedule rows to services if needed
      // Map to track linked_service_id -> actual contract_service.id after save
      const serviceIdMap = new Map<string, string>()
      // Old DSNY-derived service uuids replaced by the freshly derived set; used to
      // remap visit-group assignments onto the new Building Upkeep service.
      const replacedDsnyServiceIds: string[] = []
      let visitGroupsRelinkedAfterServices = false

      if (id) {
        try {
          // For maintenance_dsny, convert DSNY schedule rows to services
          let servicesToSave = [...contractServices]
// For DSNY maintenance, create a single "Building Maintenance" service if any line items are linked to it
          const hasDSNYPackageLineItem = lineItems.some(item => item.linked_service_id === 'dsny_package')

          if (recurringScheduleType === 'maintenance_dsny' && dsnyScheduleRows.length > 0) {
            const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
            const buildingUpkeepItem = findBuildingUpkeepItem(serviceItemsGrouped)
            const exteriorMaintenanceItem = findExteriorMaintenanceItem(serviceItemsGrouped)
            const packageIds = [
              packageItem?.id,
              buildingUpkeepItem?.id,
              exteriorMaintenanceItem?.id,
            ].filter(Boolean) as string[]

            // The existing package service (real row kept in sync by state,
            // or a temp dsny_package): keep its price and proposal link.
            const statePackageService =
              servicesToSave.find((s: any) => s.id === 'dsny_package') ||
              servicesToSave.find(
                (s: any) => s.service_item_id && packageIds.includes(s.service_item_id)
              )

            // Strip services that are rebuilt from the schedule rows so the derived
            // set is the only source (prevents duplicate package rows).
            const isScheduleDerived = (s: any) =>
              isScheduleDerivedContractService(s, buildingUpkeepItem, exteriorMaintenanceItem)
            servicesToSave
              .filter(isScheduleDerived)
              .forEach((s: any) => replacedDsnyServiceIds.push(s.id))
            servicesToSave = servicesToSave.filter((s: any) => !isScheduleDerived(s))

            // Single source of truth: schedule rows in, services out (shared with Create)
            const { services: derivedDsnyServices } = deriveDsnyContractArtifacts(
              dsnyScheduleRows as any,
              {
                buildingUpkeepItem: packageItem,
                packageKind: dsnyPackageKind,
                maintenanceDSNYCategoryId,
                proposalLineItemId:
                  (statePackageService as any)?.proposal_line_item_id ||
                  dsnyProposalLinkId ||
                  undefined,
                amount: statePackageService?.amount ?? undefined,
                forcePackage: hasDSNYPackageLineItem,
                childItemsByType: buildDsnyChildItemsByType(serviceItemsGrouped),
              }
            )
            servicesToSave = [...servicesToSave, ...derivedDsnyServices]

            // Collapse duplicate non-weekly services (e.g. a stale persisted "Drain
            // Cleaning" left in state alongside the copy freshly derived from its
            // schedule row). Without this, every save re-inserts both and the
            // duplicates multiply. Keep the copy referenced by a visit group (so its
            // mapping still resolves), otherwise the schedule-derived (`dsny_`) copy.
            const referencedServiceIds = new Set(
              visitGroups.flatMap((g) => getGroupAssignments(g).map((a) => a.service_id))
            )
            const dedupIndexByKey = new Map<string, number>()
            const dedupedServices: any[] = []
            for (const svc of servicesToSave) {
              const rtype = (svc as any).schedule?.recurrence_type || (svc as any).recurrence_type || ''
              const key = `${(svc.service_name || '').trim().toLowerCase()}|${rtype}`
              const existingIdx = dedupIndexByKey.get(key)
              if (existingIdx === undefined) {
                dedupIndexByKey.set(key, dedupedServices.length)
                dedupedServices.push(svc)
              } else {
                const existing = dedupedServices[existingIdx]
                const preferNew =
                  referencedServiceIds.has(svc.id) ||
                  (String(svc.id).startsWith('dsny_') && !referencedServiceIds.has(existing.id))
                if (preferNew) dedupedServices[existingIdx] = svc
              }
            }
            servicesToSave = dedupedServices
          }
// Replace existing contract services only when we actually have replacements.
          // Without this guard, editing a DSNY contract whose dsnyScheduleRows weren't
          // hydrated would delete all services and insert none, leaving it with zero
          // services (and un-activatable).
          if (servicesToSave.length > 0) {
// Delete existing contract services, then insert the updated set.
            const { data: svcDeletedRows, error: svcDeleteError, count: svcDeleteCount } = await supabase
              .from('contract_services')
              .delete({ count: 'exact' })
              .eq('contract_id', id)
              .select('id')
const serviceInserts = servicesToSave.map((service) => {
              // Check if there's a matching proposal line item with the same service_item_id
              const matchingProposalItem = service.service_item_id && proposalLineItems.length > 0
                ? proposalLineItems.find(
                    (propItem) => propItem.service_item_id === service.service_item_id
                  )
                : null

              if (matchingProposalItem) {
                console.log('✅ Linking contract service to proposal line item:', {
                  serviceId: service.id,
                  serviceName: service.service_name,
                  proposalLineItemId: matchingProposalItem.id,
                  serviceItemId: service.service_item_id,
                })
              }

              return {
                contract_id: id,
                // uuid columns: sentinel strings like "maintenance_dsny" poison the
                // whole multi-row insert (22P02), so coerce non-uuids to null
                service_type_id: toUuidOrNull(service.service_type),
                service_name: service.service_name,
                service_item_id: toUuidOrNull(service.service_item_id),
                amount: service.amount || 0,
                recurrence_type: service.schedule?.recurrence_type || null,
                recurrence_days: service.schedule?.recurrence_days || [],
                dsny_integration: service.schedule?.dsny_integration || false,
                garbage_schedule: service.schedule?.garbage_schedule || [],
                recycling_schedule: service.schedule?.recycling_schedule || [],
                organics_schedule: service.schedule?.organics_schedule || [],
                bulk_schedule: service.schedule?.bulk_schedule || [],
                interior_cleaning_schedule: service.schedule?.interior_cleaning_schedule || [],
                combine_target:
                  service.schedule?.combine_target === 'dsny' ||
                  service.schedule?.combine_target === 'hallway' ||
                  service.schedule?.combine_target === null
                    ? service.schedule.combine_target
                    : null,
                recurrence_weekday: service.schedule?.recurrence_weekday ?? null,
                recurrence_occurrence: service.schedule?.recurrence_occurrence ?? null,
                recurrence_day_of_month: service.schedule?.recurrence_day_of_month ?? null,
                proposal_line_item_id: (service as any).proposal_line_item_id || matchingProposalItem?.id || null,
              }
            })

            const { data: savedServices, error: servicesError } = await supabase
              .from('contract_services')
              .insert(serviceInserts)
              .select('id, service_name, service_item_id')

            if (servicesError) {
              console.error('Error saving contract services:', servicesError)
              // Don't fail the whole operation, just log the error
            } else {
              console.log('Contract services updated successfully')

              // Create mapping from linked_service_id to actual contract_service.id.
              // For previously-persisted services this maps old uuid -> new uuid.
              if (savedServices) {
                servicesToSave.forEach((service, index) => {
                  if (savedServices[index]) {
                    serviceIdMap.set(service.id, savedServices[index].id)
                  }
                })
              }

              if (recurringScheduleType === 'maintenance_dsny') {
                expandDsnyPackageServiceIdMap(serviceIdMap, dsnyScheduleRows)
                // Old DSNY service uuids collapse into the new Building Upkeep
                // package so existing visit-group assignments stay linked
                const packageUuid = serviceIdMap.get(DSNY_PACKAGE_SERVICE_ID)
                if (packageUuid) {
                  for (const oldId of replacedDsnyServiceIds) {
                    if (!serviceIdMap.has(oldId)) serviceIdMap.set(oldId, packageUuid)
                  }
                }
              }
              console.log('Service ID mapping:', serviceIdMap)

              // Re-link visit groups: deleting contract_services cascade-deleted the
              // old group->service mappings, so re-persist the groups with service
              // ids remapped onto the freshly inserted rows.
              if (formData.is_recurring) {
                try {
                  let groupsToPersist = visitGroups
                  if (recurringScheduleType === 'maintenance_dsny') {
                    if (!visitGroupsCustomized || groupsToPersist.length === 0) {
                      const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
                      groupsToPersist = buildDsnyVisitGroupsFromSchedule(dsnyScheduleRows as any, {
                        contractId: id,
                        contractStartDate: formData.start_date || null,
                        contractEndDate: formData.end_date || null,
                        contractServices: servicesToSave,
                        maintenanceCategoryId: maintenanceDSNYCategoryId,
                        packageLabel:
                          packageItem?.name ||
                          (dsnyPackageKind === 'exterior_maintenance'
                            ? 'Exterior Maintenance'
                            : 'Building Upkeep'),
                      })
                      // Keep existing UUIDs when day/target signature matches so jobs stay linked.
                      groupsToPersist = groupsToPersist.map((group) => {
                        const match = visitGroups.find(
                          (g) =>
                            !!g.dsny_integration === !!group.dsny_integration &&
                            JSON.stringify([...(g.recurrence_days || [])].sort()) ===
                              JSON.stringify([...(group.recurrence_days || [])].sort()) &&
                            (!!g.dsny_integration ||
                              /^Hallway Cleaning\b/i.test(g.name || '') ===
                                /^Hallway Cleaning\b/i.test(group.name || ''))
                        )
                        return match ? { ...group, id: match.id } : group
                      })
                    } else {
                      groupsToPersist = syncVisitGroupAssigneesFromScheduleRows(
                        groupsToPersist,
                        dsnyScheduleRows as any
                      )
                    }
                    // Relocate orphan monthly piggybacks (e.g. Drains) before persist.
                    groupsToPersist = ensureDsnyVisitGroupsForDisplay(
                      groupsToPersist,
                      servicesToSave,
                      {
                        scheduleRows: dsnyScheduleRows as any,
                        contractId: id,
                        contractStartDate: formData.start_date || null,
                        contractEndDate: formData.end_date || null,
                      }
                    )
                  }
                  if (groupsToPersist.length > 0) {
                    await replaceContractVisitGroups(id, groupsToPersist, serviceIdMap, {
                      strict: true,
                    })
                    visitGroupsRelinkedAfterServices = true
                    await fetchVisitGroups(id)
                    console.log('✅ Visit groups re-linked after service update')
                  }
                } catch (vgErr) {
                  console.error('Error re-linking visit groups after service update:', vgErr)
                  throw vgErr
                }
              }
            }
          }
        } catch (servicesError) {
          console.error('Error saving services:', servicesError)
          // Don't fail the whole operation, just log the error
        }

        // Visit-group-only edits (no service re-insert): persist with existing uuids.
        if (
          id &&
          formData.is_recurring &&
          visitGroups.length > 0 &&
          !visitGroupsRelinkedAfterServices
        ) {
          try {
            const groupsToPersist =
              recurringScheduleType === 'maintenance_dsny'
                ? ensureDsnyVisitGroupsForDisplay(
                    syncVisitGroupAssigneesFromScheduleRows(
                      visitGroups,
                      dsnyScheduleRows as any
                    ),
                    contractServicesRef.current,
                    {
                      scheduleRows: dsnyScheduleRows as any,
                      contractId: id,
                      contractStartDate: formData.start_date || null,
                      contractEndDate: formData.end_date || null,
                    }
                  )
                : visitGroups
            await replaceContractVisitGroups(id, groupsToPersist, serviceIdMap, { strict: false })
            await fetchVisitGroups(id)
          } catch (vgErr) {
            console.error('Error saving visit groups:', vgErr)
            throw vgErr
          }
        }
      }

      // Update line items
      if (id) {
        try {
          // Ensure non-weekly DSNY services (Drain, monthly Hallway, etc.) have line items
          // so job generation can attach them as piggybacks.
          const lineItemsToPersist =
            recurringScheduleType === 'maintenance_dsny'
              ? syncNonWeeklyDsnyLineItems(lineItems as any, contractServices as any)
              : lineItems

          // Fetch existing contract services to check for matches by service_item_id
          const { data: existingServices } = await supabase
            .from('contract_services')
            .select('id, service_name, service_item_id')
            .eq('contract_id', id)

          // Delete existing line items
          await supabase.from('contract_line_items').delete().eq('contract_id', id)

          // Insert new line items with contract_service_id link
          const lineItemInserts = lineItemsToPersist
            .filter((item) => item.description.trim() !== '') // Only save items with descriptions
            .map((item, index) => {
              // Map linked_service_id to actual contract_service_id
              // If item already has contract_service_id (from database), use it
              // Otherwise, try to map from linked_service_id
              let contract_service_id = item.contract_service_id
                || (item.linked_service_id ? serviceIdMap.get(item.linked_service_id) || null : null)

              // If still no link, check existing services in the database by service_item_id
              if (!contract_service_id && item.service_item_id && existingServices) {
                const matchingExistingService = existingServices.find(
                  (existingService: any) => existingService.service_item_id === item.service_item_id
                )
                if (matchingExistingService) {
                  contract_service_id = matchingExistingService.id
                  console.log('✅ Auto-linked line item to EXISTING service by service_item_id:', {
                    lineItemDescription: item.description,
                    serviceName: matchingExistingService.service_name,
                    serviceItemId: item.service_item_id,
                    contractServiceId: contract_service_id,
                  })
                }
              }

              // Note: Auto-linking to newly saved services is handled via linked_service_id
              // which is mapped through serviceIdMap above

              return {
                contract_id: id,
                contract_service_id: contract_service_id,
                service_item_id: item.service_item_id || null,
                proposal_line_item_id: item.proposal_line_item_id || null, // Link to proposal line item
                description: item.description.trim(),
                quantity: item.is_variable ? 0 : (item.quantity || 1),
                is_variable: item.is_variable || false,
                unit_type: item.unit_type || 'flat_rate',
                unit_price: item.unit_price || 0,
                total_price: item.is_variable ? 0 : ((item.quantity || 1) * (item.unit_price || 0)),
                sort_order: index, // Use index to ensure sequential sort_order
              }
            })

          if (lineItemInserts.length > 0) {
            const { error: lineItemsError } = await supabase
              .from('contract_line_items')
              .insert(lineItemInserts)

            if (lineItemsError) {
              console.error('Error saving contract line items:', lineItemsError)
              // Don't fail the whole operation, just log the error
              // Table might not exist yet - user can create it later
            } else {
              console.log('Contract line items updated successfully')
            }
          }
        } catch (lineItemsError) {
          console.error('Error saving line items:', lineItemsError)
          // Don't fail the whole operation, just log the error
        }
      }

      await logActivity({
        activity_type: 'updated',
        entity_type: 'contract',
        entity_id: id || '',
        description: `Contract ${formData.title} was updated`,
        metadata: {
          title: formData.title,
          status: formData.status,
        },
      })

      setNotification({
        type: 'success',
        message: 'Contract updated successfully!',
      })
      setTimeout(() => {
        navigate(`/contracts/details/${id}`)
      }, 1500)
    } catch (error) {
      console.error('Error updating contract:', error)
      setNotification({
        type: 'error',
        message: 'Error updating contract: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading contract details...</p>
        </div>
      </MobilePage>
    )
  }

  if (!contract) {
    return (
      <MobilePage className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-red-600">Contract not found</p>
        <button
          onClick={() => navigate('/contracts')}
          className={`mt-4 ${mobile.primaryBtn}`}
        >
          Back to Contracts
        </button>
      </MobilePage>
    )
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title="Edit Contract"
        subtitle={`Contract #${contract.contract_number}`}
        onBack={() => {
          if (returnTo) {
            navigate(decodeURIComponent(returnTo))
          } else {
            navigate('/contracts')
          }
        }}
        backLabel={returnTo ? 'Back' : 'Back to Contracts'}
        actions={
          <button
            type="button"
            onClick={() => navigate(`/contracts/details/${id}`)}
            className={mobile.secondaryBtn}
          >
            View
          </button>
        }
      />

      {/* Tab Navigation */}
      <div className={`${mobile.surface} sticky top-[3.25rem] z-10 overflow-hidden`}>
        <div className="flex overflow-x-auto scrollbar-none border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('services')}
            className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'services'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Services & Schedule
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('billing')}
            className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'billing'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Billing & Financials
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('notes')}
            className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'notes'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Notes & Activity
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={`${mobile.surface} p-4 sm:p-6 space-y-6`}>
        {consistencyIssues.length > 0 && (
          <ConsistencyWarnings
            issues={consistencyIssues}
            onNavigate={handleNavigateIssue}
            currentTab={activeTab}
          />
        )}
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Contract Title and Status - At the top */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={mobile.label}>
                  Contract Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={mobile.field}
                  placeholder="e.g., 149 Skillman Street - Maintenance"
                />
              </div>

              <div>
                <label className={mobile.label}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as typeof formData.status,
                    })
                  }
                  className={mobile.field}
                >
                  {statusDropdownOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Only workflow transitions from the current status are selectable.
                </p>
              </div>

              {/* Cancel Date - Only show when status is cancelled */}
              {formData.status === 'cancelled' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cancel Date *
                  </label>
                  <input
                    type="date"
                    required={formData.status === 'cancelled'}
                    value={formData.cancel_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cancel_date: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Jobs scheduled after this date will be automatically deleted.
                  </p>
                </div>
              )}
            </div>

            {/* Two Column Layout: Customer/Property on left, Template/Proposal on right */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column: Customer & Property */}
              <div className="flex flex-col justify-between">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900">Customer & Property</h2>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer *
                        </label>
                        <div className="flex gap-2">
                          <select
                            required
                            value={formData.customer_id}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                customer_id: e.target.value,
                                property_id: '',
                              })
                              setSelectedPropertyIds([])
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select customer...</option>
                            {customers.map((customer) => (
                              <option key={customer.id} value={customer.id}>
                                {customer.company_name ||
                                  `${customer.contact_first_name} ${customer.contact_last_name}`}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setShowCreateCustomer(true)}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                          >
                            + New
                          </button>
                        </div>
                      </div>

                      <ContractPropertyScopeFields
                        contractType={formData.contract_type as ContractType}
                        customerId={formData.customer_id}
                        scopeType={scopeType}
                        selectedPropertyIds={selectedPropertyIds}
                        properties={properties}
                        onScopeChange={(nextScope) => {
                          setScopeType(nextScope)
                          if (nextScope === 'single_property') {
                            const primary = selectedPropertyIds[0] || ''
                            setSelectedPropertyIds(primary ? [primary] : [])
                            setFormData({ ...formData, property_id: primary })
                          }
                        }}
                        onSelectionChange={(ids) => {
                          setSelectedPropertyIds(ids)
                          setFormData({ ...formData, property_id: ids[0] || '' })
                        }}
                        onCreateProperty={() => setShowCreateProperty(true)}
                      />
                    </div>
                  </div>
                </div>
                {/* Spacer to align with right column height */}
                <div className="flex-grow"></div>
              </div>

              {/* Right Column: Template Type & Proposal Selection */}
              <div className="space-y-6">
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-900">Contract Type & Proposal</h2>

                  <div className="space-y-4">
                    <div data-consistency-anchor="template-type">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Type *
                      </label>
                      <select
                        required
                        value={formData.service_type}
                        onChange={(e) => {
                          const newServiceType = e.target.value
                          const newDocData = newServiceType
                            ? buildDefaultDocumentData(newServiceType)
                            : ({} as DocumentData)
                          setFormData({
                            ...formData,
                            service_type: newServiceType,
                            template_type: newServiceType
                              ? getDefaultTemplateTypeForServiceType(newServiceType)
                              : '',
                            document_data: newDocData,
                            billing_method:
                              newDocData.pricingMode === 'rate_card'
                                ? 'actual_usage'
                                : formData.billing_method,
                          })
                          if (
                            newDocData.pricingMode === 'rate_card' &&
                            hasRateCardSeed(newServiceType)
                          ) {
                            setLineItems(
                              buildRateCardLineItems(newServiceType) as ContractLineItem[]
                            )
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Please select...</option>
                        {SERVICE_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Drives the document template and DSNY scheduling.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Create from Proposal (Optional)
                      </label>
                      <select
                        value={formData.proposal_id}
                        onChange={(e) => setFormData({ ...formData, proposal_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select a proposal...</option>
                        {filteredProposals.map((proposal) => (
                          <option key={proposal.id} value={proposal.id}>
                            {proposal.proposal_number} - {proposal.title}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Selecting a proposal will auto-fill service details and amount
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contract Description - Below the two columns */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Contract Description</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter contract description"
                />
              </div>
            </div>
          </div>
        )}

        {/* Services & Schedule Tab */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Job Assignment</h2>
              <ContractAssignmentFields
                value={{
                  assignment_source: formData.assignment_source,
                  default_assigned_crew_id: formData.default_assigned_crew_id,
                  default_assigned_employee_id: formData.default_assigned_employee_id,
                }}
                onChange={(assignment) => setFormData({ ...formData, ...assignment })}
                hasProperty={!!formData.property_id}
                propertyDefaultLabels={propertyAssignmentLabels}
                required
              />
            </div>
            {/* Contract Type */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div data-consistency-anchor="contract-type">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contract Type *
                  </label>
                  <select
                    required
                    value={formData.contract_type}
                    onChange={(e) => {
                      const newContractType = e.target.value as '' | ContractType
                      setFormData({
                        ...formData,
                        contract_type: newContractType,
                        is_recurring: newContractType === 'recurring',
                      })
                      // Reset recurring schedule type when contract type changes
                      if (newContractType !== 'recurring') {
                        setRecurringScheduleType(null)
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select type...</option>
                    <option value="one_time">One Time</option>
                    <option value="as_needed">Ongoing (As Needed)</option>
                    <option value="recurring">Recurring</option>
                  </select>
                  {formData.contract_type === 'as_needed' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Open-ended agreement. Jobs are created when needed — no auto-recurring
                      schedule.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Services & Schedule - Conditional based on contract type */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Services & Schedule</h2>
              </div>

              {formData.contract_type !== 'recurring' ? (
                /* One-time / Ongoing — manual jobs, no visit board */
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    {formData.contract_type === 'as_needed'
                      ? 'Services for this ongoing agreement. Create jobs as work is needed.'
                      : 'Services with schedules. Each service schedule creates a job.'}
                  </p>

                  {contractServices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="mb-4">No services added to this contract yet.</p>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddServiceModal(true)
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add Service & Schedule
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {contractServices.map((service) => {
                        const isExpanded = expandedServices.has(service.id)
                        return (
                          <div
                            key={service.id}
                            className="border border-gray-200 rounded-lg overflow-hidden"
                          >
                            <button
                              type="button"
                              onClick={() => toggleService(service.id)}
                              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-3">
                                <h3 className="font-medium text-gray-900">{service.service_name}</h3>
                                <p className="text-sm text-gray-600">
                                  ${service.amount?.toFixed(2) || '0.00'}
                                </p>
                                {formData.contract_type === 'one_time' ? (
                                  service.scheduled_date || service.scheduled_time ? (
                                    <span className="text-xs text-blue-600">
                                      {service.scheduled_date ? new Date(service.scheduled_date).toLocaleDateString() : ''}
                                      {service.scheduled_date && service.scheduled_time ? ' at ' : ''}
                                      {service.scheduled_time || ''}
                                    </span>
                                  ) : null
                                ) : (
                                  service.schedule?.recurrence_type && (
                                    <span className="text-xs text-blue-600">
                                      Schedule: {service.schedule.recurrence_type}
                                    </span>
                                  )
                                )}
                              </div>
                              <ChevronDownIcon
                                className={`w-5 h-5 text-gray-400 transition-transform ${
                                  isExpanded ? 'transform rotate-180' : ''
                                }`}
                              />
                            </button>
                            {isExpanded && (
                              <div className="p-4 pt-0 border-t border-gray-200">
                                {service.schedule && (
                                  <div className="mt-3">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Schedule</h4>
                                    <ScheduleView
                                      garbageSchedule={service.schedule.garbage_schedule || []}
                                      recyclingSchedule={service.schedule.recycling_schedule || []}
                                      organicsSchedule={service.schedule.organics_schedule || []}
                                      bulkSchedule={service.schedule.bulk_schedule || []}
                                      interiorCleaningSchedule={(service.schedule.interior_cleaning_schedule || []).map((day: string | number) => parseInt(String(day)))}
                                      masterWeeklySchedule={service.schedule.recurrence_days || []}
                                      manualSchedules={[]}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : (
                /* Recurring Contract - Two options */
                <div className="space-y-4">
                  {!recurringScheduleType ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 mb-4">
                        Choose the type of recurring schedule for this contract:
                      </p>
                      <button
                        type="button"
                        onClick={() => handleSelectDSNYSchedule()}
                        className="w-full p-4 text-left border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <span className="text-2xl">🧹</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Recurring Maintenance schedule with DSNY</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Automatically sync with DSNY pickup schedules
                            </p>
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={openOtherRecurringSchedule}
                        className="w-full p-4 text-left border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span className="text-2xl">🔧</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Other recurring schedule</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Create a custom recurring schedule for non-maintenance services
                            </p>
                          </div>
                        </div>
                      </button>
                    </div>
                  ) : recurringScheduleType === 'maintenance_dsny' ? (
                        /* Maintenance DSNY Schedule */
                        <div className="space-y-4">
                          {/* In-app Notification */}
                          {notification && (
                            <div className={`p-4 rounded-lg border ${
                              notification.type === 'success'
                                ? 'bg-green-50 border-green-200 text-green-800'
                                : 'bg-red-50 border-red-200 text-red-800'
                            }`}>
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium">{notification.message}</p>
                                <button
                                  onClick={() => setNotification(null)}
                                  className="ml-4 text-gray-400 hover:text-gray-600"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              DSNY package
                            </label>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  if (dsnyPackageKind === 'building_upkeep') return
                                  setDsnyPackageKind('building_upkeep')
                                  setDsnyScheduleRows((rows) =>
                                    rebaseDsnyRowsForPackageKind(
                                      'building_upkeep',
                                      rows
                                    ) as typeof dsnyScheduleRows
                                  )
                                }}
                                className={`px-3 py-1.5 text-sm rounded-md border ${
                                  dsnyPackageKind === 'building_upkeep'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                Building Upkeep
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (dsnyPackageKind === 'exterior_maintenance') return
                                  setDsnyPackageKind('exterior_maintenance')
                                  setDsnyScheduleRows((rows) =>
                                    rebaseDsnyRowsForPackageKind(
                                      'exterior_maintenance',
                                      rows
                                    ) as typeof dsnyScheduleRows
                                  )
                                }}
                                className={`px-3 py-1.5 text-sm rounded-md border ${
                                  dsnyPackageKind === 'exterior_maintenance'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                Exterior Maintenance
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {dsnyPackageKind === 'exterior_maintenance'
                                ? 'Pickups only (Garbage, Recycling, Organics, Bulk) — no hallway cleaning.'
                                : 'Includes hallway cleaning plus DSNY pickup types.'}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="font-semibold text-gray-900">Maintenance Schedule with DSNY</h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {dsnyPackageKind === 'exterior_maintenance'
                                  ? 'Configure DSNY pickup schedules for exterior maintenance'
                                  : 'Configure maintenance schedules for DSNY collection types and hallway cleaning'}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              {formData.property_id && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const selectedProperty = properties.find((p) => p.id === formData.property_id)
                                    if (!selectedProperty) {
                                      alert('Property not found. Please select a property first.')
                                      return
                                    }
                                    if (!selectedProperty.address) {
                                      alert('Selected property does not have an address. Please add an address to the property first.')
                                      return
                                    }

                                    setFetchingDSNY(true)
                                    try {
                                      console.log('Fetching DSNY schedule for:', selectedProperty.address)

                                      const dsnyData = await fetchDSNYPickupSchedule(selectedProperty.address)

                                      if (!dsnyData || !dsnyData.schedules) {
                                        throw new Error('Invalid DSNY data received')
                                      }

                                      const garbageDays = dsnyData.schedules.garbage || []
                                      const recyclingDays = dsnyData.schedules.recycling || []
                                      const organicsDays = dsnyData.schedules.organics || []
                                      const bulkDays = dsnyData.schedules.bulk || []

                                      setDsnyScheduleRows((currentRows) =>
                                        applyDsnyApiDaysToRows(
                                          dsnyPackageKind,
                                          dsnyData.schedules,
                                          currentRows
                                        ) as typeof dsnyScheduleRows
                                      )

                                      setDsnyScheduleFetched(true)

                                      const totalDays = garbageDays.length + recyclingDays.length + organicsDays.length + bulkDays.length
                                      if (totalDays > 0) {
                                        setNotification({
                                          type: 'success',
                                          message: `DSNY schedule loaded! Garbage: ${garbageDays.length} days, Recycling: ${recyclingDays.length} days, Organics: ${organicsDays.length} days, Bulk: ${bulkDays.length} days`
                                        })
                                        setTimeout(() => setNotification(null), 5000)
                                      } else {
                                        setNotification({
                                          type: 'error',
                                          message: 'DSNY schedule fetched but no collection days found. Please check the property address.'
                                        })
                                        setTimeout(() => setNotification(null), 5000)
                                      }
                                    } catch (error) {
                                      console.error('Error fetching DSNY schedule:', error)
                                      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
                                      setNotification({
                                        type: 'error',
                                        message: `Error fetching DSNY schedule: ${errorMessage}. Please check the property address, internet connection, and DSNY API accessibility.`
                                      })
                                      setTimeout(() => setNotification(null), 7000)
                                    } finally {
                                      setFetchingDSNY(false)
                                    }
                                  }}
                                  disabled={fetchingDSNY}
                                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {fetchingDSNY ? 'Fetching...' : 'Fetch DSNY Schedule'}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setRecurringScheduleType(null)
                                  setDsnyScheduleRows([])
                                  setDsnyScheduleFetched(false)
                                }}
                                className="text-sm text-gray-600 hover:text-gray-900"
                              >
                                Change Type
                              </button>
                            </div>
                          </div>

                          {!formData.property_id ? (
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-sm text-yellow-800">
                                ⚠️ Please select a property in the Overview tab to fetch DSNY schedule
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <DsnyVisitRepAssignment
                                rows={dsnyScheduleRows}
                                crews={crews}
                                employees={employees}
                                onAssign={updateDsnyRowAssignments}
                              />
                              {dsnyScheduleRows.map((row) => (
                                <div
                                  key={row.id}
                                  className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                                >
                                  <div className="flex items-start justify-between gap-2 mb-3">
                                    {row.type === 'custom' ? (
                                      <div className="flex-1 space-y-2 min-w-0">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">
                                            Catalogue item
                                          </label>
                                          <select
                                            value={row.service_item_id || ''}
                                            onChange={(e) =>
                                              selectCustomDsnyCatalogItem(row.id, e.target.value)
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                          >
                                            <option value="">Select from catalogue…</option>
                                            {customDsnyCatalogOptions.map((item) => (
                                              <option key={item.id} value={item.id}>
                                                {item.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <input
                                          type="text"
                                          value={row.name}
                                          onChange={(e) => updateDsnyScheduleRowName(row.id, e.target.value)}
                                          placeholder="Display name (optional override)"
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                      </div>
                                    ) : (
                                      <h4 className="font-medium text-gray-900">{row.name}</h4>
                                    )}
                                    {row.type === 'custom' && (
                                      <button
                                        type="button"
                                        onClick={() => removeDsnyScheduleRow(row.id)}
                                        className="ml-2 px-2 py-1 text-sm text-red-600 hover:text-red-800 flex-shrink-0"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                  {(row.type === 'custom' ||
                                    row.type === 'hallway_cleaning') && (
                                    <div className="mb-3">
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Recurrence
                                      </label>
                                      <select
                                        value={getRowRecurrenceType(row)}
                                        onChange={(e) => {
                                          const val = e.target.value as DsnyRowRecurrenceType
                                          updateDsnyScheduleRow(row.id, {
                                            recurrence_type: val,
                                            ...(val === 'monthly'
                                              ? {
                                                  monthly_mode:
                                                    row.monthly_mode || ('nth_weekday' as DsnyMonthlyMode),
                                                  recurrence_weekday: row.recurrence_weekday ?? 1,
                                                  recurrence_occurrence: row.recurrence_occurrence ?? 1,
                                                  recurrence_day_of_month:
                                                    row.monthly_mode === 'day_of_month'
                                                      ? row.recurrence_day_of_month ?? 1
                                                      : null,
                                                }
                                              : {}),
                                          })
                                        }}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      >
                                        {RECURRENCE_TYPE_OPTIONS.map((o) => (
                                          <option key={o.value} value={o.value}>
                                            {o.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {getRowRecurrenceType(row) === 'daily' ? (
                                    <p className="text-sm text-gray-600">
                                      Runs every day (no day selection needed).
                                    </p>
                                  ) : getRowRecurrenceType(row) === 'monthly' ? (
                                    <div className="space-y-3">
                                      <div className="flex flex-wrap gap-4">
                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                          <input
                                            type="radio"
                                            name={`monthly_mode_${row.id}`}
                                            checked={(row.monthly_mode || 'nth_weekday') === 'nth_weekday'}
                                            onChange={() =>
                                              updateDsnyScheduleRow(row.id, {
                                                monthly_mode: 'nth_weekday',
                                              })
                                            }
                                          />
                                          <span>On the nth weekday (e.g. 2nd Monday)</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                          <input
                                            type="radio"
                                            name={`monthly_mode_${row.id}`}
                                            checked={row.monthly_mode === 'day_of_month'}
                                            onChange={() =>
                                              updateDsnyScheduleRow(row.id, {
                                                monthly_mode: 'day_of_month',
                                              })
                                            }
                                          />
                                          <span>On a day of the month (e.g. the 15th)</span>
                                        </label>
                                      </div>

                                      {(row.monthly_mode || 'nth_weekday') === 'nth_weekday' ? (
                                        <div className="flex flex-wrap gap-2">
                                          <select
                                            value={row.recurrence_occurrence ?? 1}
                                            onChange={(e) =>
                                              updateDsnyScheduleRow(row.id, {
                                                recurrence_occurrence: parseInt(e.target.value, 10),
                                              })
                                            }
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                          >
                                            {OCCURRENCE_OPTIONS.map((o) => (
                                              <option key={o.value} value={o.value}>
                                                {o.label}
                                              </option>
                                            ))}
                                          </select>
                                          <select
                                            value={row.recurrence_weekday ?? 1}
                                            onChange={(e) =>
                                              updateDsnyScheduleRow(row.id, {
                                                recurrence_weekday: parseInt(e.target.value, 10),
                                              })
                                            }
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                          >
                                            {WEEKDAY_OPTIONS.map((o) => (
                                              <option key={o.value} value={o.value}>
                                                {o.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      ) : (
                                        <div className="space-y-2">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-700">Day of month</span>
                                            <input
                                              type="number"
                                              min={1}
                                              max={31}
                                              value={row.recurrence_day_of_month ?? 1}
                                              onChange={(e) =>
                                                updateDsnyScheduleRow(row.id, {
                                                  recurrence_day_of_month: Math.min(
                                                    31,
                                                    Math.max(1, parseInt(e.target.value, 10) || 1)
                                                  ),
                                                })
                                              }
                                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                            />
                                          </div>
                                          {resolveDsnyCombineTarget(row) != null && (
                                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={!!row.snap_to_closest_visit}
                                                onChange={(e) =>
                                                  updateDsnyScheduleRow(row.id, {
                                                    snap_to_closest_visit: e.target.checked,
                                                  })
                                                }
                                                className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                              />
                                              <span>Generate on the closest existing visit day</span>
                                            </label>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex flex-wrap gap-2">
                                      {dayNames.map((day) => {
                                        const selected = (row.days || []).includes(day.key)
                                        return (
                                          <button
                                            key={day.key}
                                            type="button"
                                            aria-pressed={selected}
                                            onClick={() => toggleDayForRow(row.id, day.key)}
                                            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                              selected
                                                ? 'bg-green-100 border-green-500 text-green-800'
                                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                          >
                                            {day.label}
                                          </button>
                                        )
                                      })}
                                    </div>
                                  )}
                                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Visit target {row.type === 'custom' ? '*' : ''}
                                      </label>
                                      <select
                                        value={visitTargetSelectValue(row)}
                                        onChange={(e) =>
                                          updateDsnyScheduleRow(row.id, {
                                            combine_target: visitTargetFromSelectValue(
                                              e.target.value
                                            ),
                                          })
                                        }
                                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                          dsnyRowNeedsCombineTarget(row)
                                            ? 'border-amber-400'
                                            : 'border-gray-300'
                                        }`}
                                      >
                                        {row.type === 'custom' && (
                                          <option value="">Select visit target…</option>
                                        )}
                                        <option value="separate">Separate visit</option>
                                        <option value="dsny">Combine with DSNY Exterior</option>
                                        <option value="hallway">Combine with Hallway visits</option>
                                      </select>
                                    </div>
                                    {resolveDsnyCombineTarget(row) === null && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">
                                            Crew
                                          </label>
                                          <select
                                            value={row.assigned_crew_id || ''}
                                            onChange={(e) =>
                                              updateDsnyRowAssignments([row.id], {
                                                assigned_crew_id: e.target.value || null,
                                              })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                          >
                                            <option value="">Inherit from contract / property</option>
                                            {crews.map((crew) => (
                                              <option key={crew.id} value={crew.id}>
                                                {crew.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">
                                            Employee
                                          </label>
                                          <select
                                            value={row.assigned_employee_id || ''}
                                            onChange={(e) =>
                                              updateDsnyRowAssignments([row.id], {
                                                assigned_employee_id: e.target.value || null,
                                              })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                          >
                                            <option value="">Inherit from contract / property</option>
                                            {employees.map((employee) => (
                                              <option key={employee.id} value={employee.id}>
                                                {formatEmployeeLabel(employee)}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={addDsnyScheduleRow}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                              >
                                <PlusIcon className="w-4 h-4" />
                                Add Another Schedule Line
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                    /* Other Recurring Schedule */
                    <div className="space-y-4" data-consistency-anchor="other-schedule">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">Other Recurring Schedule</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Set how often each service runs — pick days of the week so crew visits
                            can be built.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setRecurringScheduleType(null)}
                          className="text-sm text-gray-600 hover:text-gray-900"
                        >
                          Change Type
                        </button>
                      </div>

                      {contractServices.length > 0 &&
                        contractServices.some((s) => serviceNeedsRecurringSchedule(s)) && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 mb-2">
                            {contractServices.filter((s) => serviceNeedsRecurringSchedule(s)).length}{' '}
                            service
                            {contractServices.filter((s) => serviceNeedsRecurringSchedule(s))
                              .length === 1
                              ? ''
                              : 's'}{' '}
                            still need a recurring schedule (days / cadence). Use{' '}
                            <span className="font-medium">Set schedule</span> on each row.
                          </div>
                        )}

                      {contractServices.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                          <p className="mb-4">No services added to the schedule yet.</p>
                          <button
                            type="button"
                            onClick={() => {
                              const defaultRecurrenceType =
                                formData.contract_type === 'one_time' ? 'none' : 'weekly'
                              setNewService({
                                service_type: '',
                                service_name: '',
                                amount: 0,
                                description: '',
                                scheduled_date: '',
                                scheduled_time: '',
                                recurrence_type: defaultRecurrenceType,
                                recurrence_days: [],
                                recurrence_day_of_month: null,
                                recurrence_weekday: null,
                                recurrence_occurrence: null,
                                recurrence_month: null,
                                recurrence_interval_months: 1,
                                recurrence_interval_weeks: 1,
                                recurrence_interval_days: 1,
                                dsny_integration: false,
                                garbage_schedule: [],
                                recycling_schedule: [],
                                organics_schedule: [],
                                bulk_schedule: [],
                                interior_cleaning_schedule: [],
                              })
                              setEditingServiceId(null)
                              setShowAddServiceModal(true)
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Add Service
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {contractServices.map((service) => {
                            const needsSchedule = serviceNeedsRecurringSchedule(service)
                            const scheduleLabel = describeContractServiceSchedule(
                              service as any,
                              visitGroups
                            )
                            return (
                              <div
                                key={service.id}
                                data-consistency-anchor={`service-${service.id}`}
                                className={`p-4 border rounded-lg transition-colors ${
                                  needsSchedule
                                    ? 'border-amber-300 bg-amber-50/40'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-gray-900">
                                      {service.service_name}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      ${service.amount?.toFixed(2) || '0.00'}
                                    </p>
                                    {scheduleLabel && !needsSchedule ? (
                                      <p className="text-xs text-blue-600 mt-1">
                                        Schedule: {scheduleLabel}
                                      </p>
                                    ) : needsSchedule ? (
                                      <p className="text-xs text-amber-700 mt-1 font-medium">
                                        {(service.schedule?.recurrence_type ||
                                          (service as any).recurrence_type)
                                          ? `Marked ${String(
                                              service.schedule?.recurrence_type ||
                                                (service as any).recurrence_type
                                            ).replace(/_/g, ' ')} — pick days to build crew visits`
                                          : 'No recurring schedule set yet'}
                                      </p>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => handleEditService(service.id)}
                                      className={`px-3 py-1 text-sm rounded transition-colors ${
                                        needsSchedule
                                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                                          : 'border border-gray-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      {needsSchedule ? 'Set schedule' : 'Edit'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveService(service.id)}
                                      className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50 transition-colors"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                          <button
                            type="button"
                            onClick={() => {
                              setNewService({
                                service_type: '',
                                service_name: '',
                                amount: 0,
                                description: '',
                                scheduled_date: '',
                                scheduled_time: '',
                                recurrence_type: 'weekly',
                                recurrence_days: [],
                                recurrence_day_of_month: null,
                                recurrence_weekday: null,
                                recurrence_occurrence: null,
                                recurrence_month: null,
                                recurrence_interval_months: 1,
                                recurrence_interval_weeks: 1,
                                recurrence_interval_days: 1,
                                dsny_integration: false,
                                garbage_schedule: [],
                                recycling_schedule: [],
                                organics_schedule: [],
                                bulk_schedule: [],
                                interior_cleaning_schedule: [],
                              })
                              setEditingServiceId(null)
                              setShowAddServiceModal(true)
                            }}
                            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-700 rounded-lg hover:border-blue-400 hover:text-blue-700 transition-colors flex items-center justify-center gap-2"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Add Service
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Crew Visits - visit groups shown alongside the schedule they derive from */}
            {formData.contract_type === 'recurring' && (
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Crew Visits</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Each line is one recurring crew visit that generates jobs on the calendar.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {visitGroupsCustomized && (
                      <button
                        type="button"
                        onClick={handleReDeriveVisitGroups}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Reset to schedule
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowVisitBoard(!showVisitBoard)}
                      className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      {showVisitBoard ? 'Hide customization' : 'Customize visits'}
                    </button>
                  </div>
                </div>

                {visitGroupsCustomized && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                    Visits were customized manually, so schedule changes above no longer update
                    them automatically. Use &quot;Reset to schedule&quot; to rebuild.
                  </p>
                )}

                {visitGroups.length === 0 ? (
                  <p className="text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                    No crew visits configured yet. Use "Customize visits" to set them up.
                  </p>
                ) : (
                  !showVisitBoard && (
                    <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                      {visitGroups.map((group) => {
                        const assignmentCount = getGroupAssignments(group).length
                        return (
                          <li
                            key={group.id}
                            className="px-4 py-2.5 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {group.name || 'Visit'}
                              </span>
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                {describeVisitGroupCadence(
                                  group,
                                  getEffectiveServicesForVisitGroups() as any
                                )}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              {assignmentCount} service{assignmentCount === 1 ? '' : 's'}
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  )
                )}

                {showVisitBoard && (
                  <div className="space-y-4 mt-2">
                    <VisitScheduleBoard
                      visitGroups={visitGroups}
                      onChange={handleVisitGroupsManualChange}
                      contractServices={getEffectiveServicesForVisitGroups()}
                      contractStartDate={formData.start_date}
                      contractEndDate={formData.end_date}
                      contractId={id}
                      onReDerive={handleReDeriveVisitGroups}
                      onAdvancedSettings={(groupId) => setAdvancedVisitGroupId(groupId)}
                      templates={visitGroupTemplates}
                      showSaveButton
                      onSave={saveVisitGroups}
                      saving={savingVisitGroups}
                    />
                    {advancedVisitGroupId && (
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-900">
                            Checklist &amp; advanced
                          </h3>
                          <button
                            type="button"
                            onClick={() => setAdvancedVisitGroupId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Close
                          </button>
                        </div>
                        <VisitGroupEditor
                          visitGroups={visitGroups.filter((g) => g.id === advancedVisitGroupId)}
                          onChange={(updated) => {
                            const updatedGroup = updated[0]
                            if (!updatedGroup) return
                            handleVisitGroupsManualChange(
                              visitGroups.map((g) => (g.id === updatedGroup.id ? updatedGroup : g))
                            )
                          }}
                          contractServices={getEffectiveServicesForVisitGroups()}
                          contractStartDate={formData.start_date}
                          contractEndDate={formData.end_date}
                          contractId={id}
                          showAddButton={false}
                          scheduleDriven={recurringScheduleType === 'maintenance_dsny'}
                          crewOptions={crews}
                          employeeOptions={employees}
                          title=""
                          subtitle=""
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Legacy Services & Schedule Tab - Replaced above */}
        {false && (
          <div className="space-y-6">
            {/* Contract Type */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div data-consistency-anchor="contract-type">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contract Type *
                  </label>
                  <select
                    required
                    value={formData.contract_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contract_type: e.target.value as '' | ContractType,
                        is_recurring: e.target.value === 'recurring',
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select type...</option>
                    <option value="one_time">One Time</option>
                    <option value="as_needed">Ongoing (As Needed)</option>
                    <option value="recurring">Recurring</option>
                  </select>
                </div>

                {formData.is_recurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recurrence Type
                    </label>
                    <select
                      value={formData.recurrence_type}
                      onChange={(e) =>
                        setFormData({ ...formData, recurrence_type: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="custom">Custom Days</option>
                    </select>
                  </div>
                )}

                {formData.is_recurring && (
                  <div data-consistency-anchor="billing-frequency">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Frequency *
                    </label>
                    <select
                      value={formData.billing_frequency}
                      onChange={(e) =>
                        setFormData({ ...formData, billing_frequency: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select frequency...</option>
                      <option value="one_time">One Time</option>
                      <option value="weekly">Weekly</option>
                      <option value="bi_weekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>
                )}

                {formData.is_recurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {formData.is_recurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>

              {formData.is_recurring && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">DSNY Integration</h3>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="dsny_integration_schedule"
                      checked={formData.dsny_integration}
                      onChange={(e) =>
                        setFormData({ ...formData, dsny_integration: e.target.checked })
                      }
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label
                      htmlFor="dsny_integration_schedule"
                      className="text-sm font-medium text-gray-700"
                    >
                      Sync with DSNY pickup schedule
                    </label>
                  </div>
                </div>
              )}

              {formData.is_recurring && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Recurring Days
                  </label>
                  <div className="grid grid-cols-7 gap-1.5">
                    {dayNames.map((day) => {
                      const selected = formData.recurrence_days.includes(day.key)
                      return (
                        <button
                          key={day.key}
                          type="button"
                          onClick={() => {
                            const newDays = selected
                              ? formData.recurrence_days.filter((d) => d !== day.key)
                              : [...formData.recurrence_days, day.key]
                            setFormData({ ...formData, recurrence_days: newDays })
                          }}
                          className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                            selected
                              ? 'bg-blue-100 text-blue-700 border-blue-300'
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {day.label.slice(0, 3)}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
{/* Crew Visits - visit groups shown alongside the schedule they derive from */}
            {formData.contract_type === 'recurring' && (
              <div className="bg-white p-6 rounded-lg border border-gray-200">
<div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Crew Visits</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Each line is one recurring crew visit that generates jobs on the calendar.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowVisitBoard(!showVisitBoard)}
                    className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex-shrink-0"
                  >
                    {showVisitBoard ? 'Hide customization' : 'Customize visits'}
                  </button>
                </div>

                {visitGroups.length === 0 ? (
                  <p className="text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                    No crew visits configured yet. Use "Customize visits" to set them up.
                  </p>
                ) : (
                  !showVisitBoard && (
                    <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                      {visitGroups.map((group) => {
                        const assignmentCount = getGroupAssignments(group).length
                        return (
                          <li
                            key={group.id}
                            className="px-4 py-2.5 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {group.name || 'Visit'}
                              </span>
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                {describeVisitGroupCadence(group)}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              {assignmentCount} service{assignmentCount === 1 ? '' : 's'}
                            </span>
                          </li>
                        )
                      })}
                    </ul>
                  )
                )}

                {showVisitBoard && (
                  <div className="space-y-4 mt-2">
                    <VisitScheduleBoard
                      visitGroups={visitGroups}
                      onChange={setVisitGroups}
                      contractServices={getEffectiveServicesForVisitGroups()}
                      contractStartDate={formData.start_date}
                      contractEndDate={formData.end_date}
                      contractId={id}
                      onAdvancedSettings={(groupId) => setAdvancedVisitGroupId(groupId)}
                    />
                    {advancedVisitGroupId && (
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-900">
                            Checklist &amp; advanced
                          </h3>
                          <button
                            type="button"
                            onClick={() => setAdvancedVisitGroupId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Close
                          </button>
                        </div>
                        <VisitGroupEditor
                          visitGroups={visitGroups.filter((g) => g.id === advancedVisitGroupId)}
                          onChange={(updated) => {
                            const updatedGroup = updated[0]
                            if (!updatedGroup) return
                            setVisitGroups(
                              visitGroups.map((g) => (g.id === updatedGroup.id ? updatedGroup : g))
                            )
                          }}
                          contractServices={getEffectiveServicesForVisitGroups()}
                          contractStartDate={formData.start_date}
                          contractEndDate={formData.end_date}
                          contractId={id}
                          showAddButton={false}
                          scheduleDriven={recurringScheduleType === 'maintenance_dsny'}
                          crewOptions={crews}
                          employeeOptions={employees}
                          title=""
                          subtitle=""
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Billing & Financials Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* In-app Notification */}
            {notification && (
              <div className={`p-4 rounded-lg border ${
                notification.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{notification.message}</p>
                  <button
                    onClick={() => setNotification(null)}
                    className="ml-4 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {/* 1. LINE ITEMS SECTION */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Define billable items for this contract. Items with Qty=0 are variable (billed based on actual usage).
                  </p>
                </div>
                <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={openServiceSelectionModal}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      title="Select services from Services & Schedule tab to add as line items"
                    >
                      <ArrowDownIcon className="w-4 h-4" />
                      Add from Services
                    </button>
                  <button
                    type="button"
                    onClick={addLineItem}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Line Item
                  </button>
                </div>
              </div>

              <PricingPresentationFields
                serviceType={formData.service_type}
                pricingMode={formData.document_data?.pricingMode}
                onPricingModeChange={handlePricingModeChange}
                onSeedRateCard={handleSeedRateCard}
                lineItemCount={lineItems.length}
              />

              {lineItems.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500 mb-4">No line items added yet.</p>
                  <p className="text-sm text-gray-400">
                    Click "Add Line Item" to create a new billing item, or "Add from Services" to import from the Services & Schedule tab.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {lineItems.map((item, index) => (
                  <div
                    key={item.id}
                    className={`rounded-lg p-4 border ${
                      item.linked_service_id
                        ? 'bg-purple-50 border-purple-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {item.linked_service_id && (
                      <div className="mb-2 flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Linked to Service
                        </span>
                      </div>
                    )}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Link to Service Catalog{' '}
                        <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <ServiceItemSelect
                        value={item.service_item_id || ''}
                        onChange={(id) => handleServiceItemSelect(index, id)}
                        grouped={serviceItemsGrouped}
                        allowEmpty
                        emptyValueLabel="None - Custom Entry"
                        onCatalogRefresh={fetchServiceItems}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 items-start">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description *
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) =>
                            handleLineItemChange(index, 'description', e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Service description"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 relative pr-24">
                          Quantity
                          <div className="absolute right-0 -top-1 flex items-start gap-1.5">
                            <input
                              type="checkbox"
                              id={`variable-${item.id}`}
                              checked={item.is_variable}
                              onChange={(e) =>
                                handleLineItemChange(index, 'is_variable', e.target.checked)
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                            />
                            <label htmlFor={`variable-${item.id}`} className="text-xs text-gray-600 font-normal leading-tight">
                              Variable<br />Quantity
                            </label>
                          </div>
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) =>
                            handleLineItemChange(
                              index,
                              'quantity',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          disabled={item.is_variable}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            item.is_variable
                              ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'border-gray-300'
                          }`}
                          placeholder={item.is_variable ? "Variable" : "0"}
                        />
                        {item.is_variable && (
                          <p className="mt-1 text-xs text-gray-500">
                            Based on actual usage
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Unit Type
                        </label>
                        <LineItemUnitTypeSelect
                          value={item.unit_type}
                          contractType={(formData.contract_type || 'one_time') as ContractType}
                          onChange={(value) =>
                            handleLineItemChange(index, 'unit_type', value)
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Unit Price
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) =>
                            handleLineItemChange(
                              index,
                              'unit_price',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total
                          </label>
                          <div className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                            item.is_variable
                              ? 'bg-orange-50 border-orange-300 text-orange-700'
                              : 'bg-white border-gray-300'
                          }`}>
                            {item.is_variable ? `$${(item.unit_price || 0).toFixed(2)} (Variable)` : `$${item.total_price.toFixed(2)}`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="p-2 text-red-600 hover:text-red-800 transition-colors"
                          title="Remove item"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {item.is_variable && (
                      <p className="mt-2 text-xs text-orange-600">
                        ⚠️ Variable item: Invoice amount will be calculated based on actual {item.unit_type.replace('_', ' ')}
                      </p>
                    )}
                  </div>
                  ))}
                </div>
              )}

              {/* Line Items Summary */}
              {lineItems.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-lg font-semibold text-gray-900">Fixed Line Items Total:</span>
                      {calculateBillingAmounts().variableCount > 0 && (
                        <p className="text-sm text-orange-600 mt-1">
                          + {calculateBillingAmounts().variableCount} variable item(s) billed based on usage
                        </p>
                      )}
                    </div>
                    <span className="text-2xl font-bold text-blue-600">
                      ${calculateBillingAmounts().fixedTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Contract document (PDF) */}
            {formData.service_type && (
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract document (PDF)</h2>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scope of Work (intro shown under Compensation)
                  </label>
                  <textarea
                    rows={3}
                    value={formData.scope_of_work}
                    onChange={(e) => setFormData({ ...formData, scope_of_work: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional narrative describing the work to be performed"
                  />
                </div>
                <DocumentDataEditor
                  serviceType={formData.service_type}
                  phase="contract"
                  value={formData.document_data}
                  onChange={(dd) => setFormData({ ...formData, document_data: dd })}
                />
              </div>
            )}

            {/* 2. CONTRACT DURATION SECTION */}
            <div className="bg-white p-6 rounded-lg border border-gray-200" data-consistency-anchor="contract-dates">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-lg font-semibold text-gray-900">Contract Duration</h2>
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                  <button
                    type="button"
                    onClick={() => setDurationMode('months')}
                    className={`px-3 py-1.5 ${durationMode === 'months' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    Number of months
                  </button>
                  <button
                    type="button"
                    onClick={() => setDurationMode('end_date')}
                    className={`px-3 py-1.5 border-l border-gray-300 ${durationMode === 'end_date' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    Specific end date
                  </button>
                </div>
              </div>
              {formData.contract_type === 'one_time' && (
                <p className="text-sm text-gray-600 mb-4">
                  Contract start and end dates are optional for one-time jobs. Optional scheduled
                  dates on each service (Services tab) control when that work is planned.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date{formData.contract_type === 'recurring' ? ' *' : ''}
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {durationMode === 'months' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (months){formData.contract_type === 'recurring' ? ' *' : ''}
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={durationMonths}
                      onChange={(e) =>
                        setDurationMonths(Math.max(1, parseInt(e.target.value, 10) || 1))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {formData.start_date && formData.end_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Ends {formData.end_date} ({durationMonths} month
                        {durationMonths === 1 ? '' : 's'})
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date {formData.contract_type === 'recurring' ? '*' : ''}
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {formData.start_date && formData.end_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Duration: {Math.ceil((new Date(formData.end_date).getTime() - new Date(formData.start_date).getTime()) / (1000 * 60 * 60 * 24))} days
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* 3. BILLING CONFIGURATION SECTION */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing Configuration</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div data-consistency-anchor="billing-frequency">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Frequency *
                  </label>
                  <select
                    value={formData.billing_frequency}
                    onChange={(e) => {
                      const billing_frequency = e.target.value
                      const updates: any = { billing_frequency }
                      if (billing_frequency === 'progress') {
                        updates.billing_method = 'progress'
                        if (billingMilestones.length === 0) {
                          setBillingMilestones([createEmptyMilestone(0)])
                        }
                      }
                      setFormData({ ...formData, ...updates })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select frequency...</option>
                    <option value="one_time">One-Time (Single Invoice)</option>
                    <option value="after_each_service">After Each Service</option>
                    <option value="progress">Progress (Milestone Billing)</option>
                    <option value="weekly">Weekly</option>
                    <option value="bi_weekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                  {formData.start_date && formData.end_date && formData.billing_frequency !== 'one_time' && formData.billing_frequency !== 'after_each_service' && (
                    <p className="text-xs text-gray-500 mt-1">
                      ≈ {calculateBillingPeriods()} billing periods during contract duration
                    </p>
                  )}
                </div>

                <BillingDayOfMonthSelect
                  billingFrequency={formData.billing_frequency}
                  value={(formData as any).billing_day_of_month ?? 1}
                  onChange={(day) =>
                    setFormData({ ...formData, billing_day_of_month: day } as typeof formData)
                  }
                />

                {formData.billing_frequency !== 'one_time' &&
                  formData.billing_frequency !== 'after_each_service' &&
                  formData.billing_frequency !== 'progress' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Method *
                    </label>
                    <select
                      value={formData.billing_method}
                      onChange={(e) => setFormData({
                        ...formData,
                        billing_method: e.target.value as 'per_period' | 'divide_total' | 'actual_usage'
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="per_period">Amount per Billing Period</option>
                      <option value="divide_total">Divide Total Contract Amount</option>
                      {calculateBillingAmounts().hasVariableItems && (
                        <option value="actual_usage">Based on Actual Usage</option>
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.billing_method === 'per_period' && 'Each invoice = Line Items Total'}
                      {formData.billing_method === 'divide_total' && 'Each invoice = Contract Total ÷ Number of Periods'}
                      {formData.billing_method === 'actual_usage' && 'Invoices generated based on recorded time/quantity'}
                    </p>
                  </div>
                )}
              </div>

              {formData.billing_frequency === 'progress' && (
                <MilestoneScheduleEditor
                  className="mt-6 p-4 bg-white border border-gray-200 rounded-lg"
                  milestones={billingMilestones}
                  totalAmount={
                    Number(formData.contract_total_amount) || Number(formData.total_amount) || 0
                  }
                  onChange={setBillingMilestones}
                />
              )}

              {/* Billing Amount Configuration */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Billing Amounts</h3>

                {formData.billing_method === 'divide_total' && formData.billing_frequency !== 'one_time' && formData.billing_frequency !== 'after_each_service' ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Contract Amount *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.contract_total_amount}
                          onChange={(e) => {
                            const total = parseFloat(e.target.value) || 0
                            const periods = calculateBillingPeriods()
                            setFormData({
                              ...formData,
                              contract_total_amount: total,
                              total_amount: periods > 0 ? total / periods : total
                            })
                          }}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Full contract value</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ÷ Billing Periods
                      </label>
                      <div className="px-3 py-2 bg-white rounded-lg border border-gray-300 text-sm">
                        {calculateBillingPeriods() || '—'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        = Amount Per Invoice
                      </label>
                      <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-300 text-lg font-bold text-blue-600">
                        ${formData.total_amount.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ) : formData.billing_method === 'actual_usage' ? (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <h4 className="font-medium text-orange-800 mb-2">Variable Billing</h4>
                    <p className="text-sm text-orange-700">
                      Invoices will be generated based on actual recorded usage (time, visits, sq ft, etc.).
                      Fixed-price line items will be billed as specified. Variable items (Qty=0) will be calculated from job records.
                    </p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Fixed items per invoice:</span>
                        <span className="ml-2 font-semibold">${calculateBillingAmounts().fixedTotal.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Variable items:</span>
                        <span className="ml-2 font-semibold">{calculateBillingAmounts().variableCount} item(s)</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.billing_frequency === 'one_time' ? 'Total Amount' : 'Amount Per Invoice'} *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.total_amount}
                          onChange={(e) => setFormData({
                            ...formData,
                            total_amount: parseFloat(e.target.value) || 0
                          })}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Line items total: ${calculateBillingAmounts().lineItemsTotal.toFixed(2)}
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, total_amount: calculateBillingAmounts().lineItemsTotal })}
                          className="ml-2 text-blue-600 hover:underline"
                        >
                          Use this
                        </button>
                      </p>
                    </div>
                    {formData.billing_frequency !== 'one_time' && formData.billing_frequency !== 'after_each_service' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estimated Contract Total
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg border border-gray-300 text-lg font-semibold">
                          ${(formData.total_amount * calculateBillingPeriods()).toFixed(2)}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.total_amount.toFixed(2)} × {calculateBillingPeriods()} periods
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* 4. PAYMENT TERMS SECTION */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Terms</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Terms *
                  </label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Late Fee %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.late_fee_percentage}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        late_fee_percentage: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Applied to overdue invoices</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sales Tax Status
                  </label>
                  <select
                    value={formData.sales_tax_status}
                    onChange={(e) => setFormData({
                      ...formData,
                      sales_tax_status: e.target.value as '' | 'taxable' | 'exempt'
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Use Customer Default</option>
                    <option value="taxable">Taxable</option>
                    <option value="exempt">Tax Exempt</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <div className="space-y-6">
            {/* Notes Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add Note</label>
                <div className="flex gap-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={3}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter a note (Ctrl+Enter to submit)"
                  />
                  <button
                    type="button"
                    onClick={addNote}
                    disabled={!newNote.trim() || savingNote}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingNote ? 'Saving...' : 'Add'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Press Ctrl+Enter to quickly submit</p>
              </div>

              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Notes History</h3>
                {notes.length === 0 ? (
                  <p className="text-sm text-gray-500">No notes added yet</p>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-xs text-gray-500">
                            {new Date(note.timestamp).toLocaleString()}
                          </p>
                          <button
                            type="button"
                            onClick={() => removeNote(index)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                        <p className="text-sm text-gray-900">{note.note}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Activity Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Activity</h2>
              {activities.length > 0 ? (
                <div className="space-y-3">
                  {activities.map((activity) => (
                    <div key={activity.id} className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No recent activity</p>
              )}
            </div>
          </div>
        )}

        {/* Service Selection Modal */}
        {showServiceSelectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Select Services to Add as Line Items</h3>
                <button
                  onClick={() => {
                    setShowServiceSelectionModal(false)
                    setSelectedServicesForImport(new Set())
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Regular Services */}
                {contractServices.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Services</h4>
                    <div className="space-y-2">
                      {contractServices.map((service) => (
                        <label
                          key={service.id}
                          className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedServicesForImport.has(service.id)}
                            onChange={() => toggleServiceSelection(service.id)}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {service.service_name || service.description || 'Unnamed Service'}
                            </div>
                            {service.amount > 0 && (
                              <div className="text-sm text-gray-500">
                                ${service.amount.toFixed(2)}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* DSNY Maintenance Package */}
                {recurringScheduleType === 'maintenance_dsny' && dsnyScheduleRows.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Maintenance Package</h4>
                    <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedServicesForImport.has('dsny_package')}
                        onChange={() => toggleServiceSelection('dsny_package')}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)?.name ||
                            (dsnyPackageKind === 'exterior_maintenance'
                              ? 'Exterior Maintenance'
                              : 'Building Upkeep')}
                        </div>
                        <div className="text-sm text-gray-500">
                          Includes: {dsnyScheduleRows.map(r => r.name).join(', ')}
                        </div>
                      </div>
                    </label>
                  </div>
                )}

                {contractServices.length === 0 && recurringScheduleType !== 'maintenance_dsny' && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No services available. Please add services in the Services & Schedule tab first.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowServiceSelectionModal(false)
                    setSelectedServicesForImport(new Set())
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={importSelectedServicesAsLineItems}
                  disabled={selectedServicesForImport.size === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Selected ({selectedServicesForImport.size})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Service Modal */}
        {showAddServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {editingServiceId
                  ? (formData.contract_type === 'one_time'
                      ? 'Edit Service & Schedule'
                      : recurringScheduleType === 'maintenance_dsny'
                      ? 'Edit Maintenance Service with DSNY'
                      : 'Edit Service in Recurring Schedule')
                  : (formData.contract_type === 'one_time'
                      ? 'Add Service & Schedule'
                      : recurringScheduleType === 'maintenance_dsny'
                      ? 'Add Maintenance Service with DSNY'
                      : 'Add Service to Recurring Schedule')}
              </h2>

              <div className="space-y-4">
                {/* Simplified UI for One Time contracts */}
                {formData.contract_type === 'one_time' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Item *
                      </label>
                      <ServiceItemSelect
                        value={newService.service_type}
                        onChange={(serviceItemId, meta) => {
                          setNewService({
                            ...newService,
                            service_type: serviceItemId,
                            service_name: meta?.name || '',
                            amount: meta?.base_price || 0,
                            description: meta?.name || '',
                          })
                        }}
                        grouped={serviceItemsGrouped}
                        onCatalogRefresh={fetchServiceItems}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newService.amount}
                        onChange={(e) =>
                          setNewService({ ...newService, amount: parseFloat(e.target.value) || 0 })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <textarea
                        value={newService.description}
                        onChange={(e) =>
                          setNewService({ ...newService, description: e.target.value })
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Service description"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Schedule <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Date</label>
                          <input
                            type="date"
                            value={newService.scheduled_date}
                            onChange={(e) =>
                              setNewService({ ...newService, scheduled_date: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Time</label>
                          <input
                            type="time"
                            value={newService.scheduled_time}
                            onChange={(e) =>
                              setNewService({ ...newService, scheduled_time: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Recurring contract fields */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Item *
                      </label>
                      <ServiceItemSelect
                        value={newService.service_type}
                        onChange={(serviceItemId, meta) => {
                          setNewService((prev) => ({
                            ...prev,
                            service_type: serviceItemId,
                            service_name: meta?.name || '',
                            amount: meta?.base_price || 0,
                            dsny_integration:
                              recurringScheduleType === 'maintenance_dsny'
                                ? true
                                : prev.dsny_integration,
                            ...(formData.contract_type === 'recurring' &&
                            recurringScheduleType !== 'maintenance_dsny' &&
                            serviceItemId &&
                            (!prev.recurrence_type || prev.recurrence_type === 'none')
                              ? { recurrence_type: 'weekly' as const }
                              : {}),
                          }))
                        }}
                        grouped={serviceItemsGrouped}
                        flatItems={serviceItems}
                        showPrice={false}
                        onCatalogRefresh={fetchServiceItems}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newService.amount}
                        onChange={(e) =>
                          setNewService({ ...newService, amount: parseFloat(e.target.value) || 0 })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </>
                )}

                {/* DSNY Integration - Only for Maintenance DSNY recurring schedule type, never for "Other" or One Time */}
                {formData.contract_type === 'recurring' && recurringScheduleType === 'maintenance_dsny' && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="dsny_integration_new"
                          checked={true}
                          onChange={(e) =>
                            setNewService({ ...newService, dsny_integration: e.target.checked })
                          }
                          disabled={true}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
                        />
                        <label
                          htmlFor="dsny_integration_new"
                          className="text-sm font-medium text-gray-700"
                        >
                          Enable DSNY Schedule Integration
                          <span className="text-xs text-gray-500 ml-2">(Required for Maintenance DSNY)</span>
                        </label>
                      </div>
                      {(recurringScheduleType === 'maintenance_dsny' || newService.dsny_integration) && formData.property_id && (
                        <button
                          type="button"
                          onClick={async () => {
                            const selectedProperty = properties.find(
                              (p) => p.id === formData.property_id
                            )
                            if (!selectedProperty) {
                              alert('Please select a property first')
                              return
                            }

                            try {
                              const dsnyData = await fetchDSNYPickupSchedule(
                                selectedProperty.address || ''
                              )
                              const garbageDays = dsnyData.schedules.garbage || []
                              const recyclingDays = dsnyData.schedules.recycling || []
                              const organicsDays = dsnyData.schedules.organics || []
                              const bulkDays = dsnyData.schedules.bulk || []

                              const allCollectionDays = Array.from(
                                new Set([
                                  ...garbageDays,
                                  ...recyclingDays,
                                  ...organicsDays,
                                  ...bulkDays,
                                ])
                              )

                              setNewService({
                                ...newService,
                                recurrence_days:
                                  allCollectionDays.length > 0
                                    ? allCollectionDays
                                    : newService.recurrence_days,
                                garbage_schedule: garbageDays,
                                recycling_schedule: recyclingDays,
                                organics_schedule: organicsDays,
                                bulk_schedule: bulkDays,
                                interior_cleaning_schedule: [],
                              })

                              alert(
                                `DSNY schedule loaded for ${selectedProperty.address}!\nGarbage: ${garbageDays.length} days\nRecycling: ${recyclingDays.length} days\nOrganics: ${organicsDays.length} days\nBulk: ${bulkDays.length} days`
                              )
                            } catch (error) {
                              console.error('Error fetching DSNY schedule:', error)
                              alert('Error fetching DSNY schedule')
                            }
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Fetch DSNY Schedule
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      When enabled, this maintenance service will automatically sync with DSNY
                      pickup schedules for garbage, recycling, organics, and bulk collection.
                    </p>
                    {(recurringScheduleType === 'maintenance_dsny' || newService.dsny_integration) && !formData.property_id && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                        ⚠️ Please select a property in the Overview tab to fetch DSNY schedule
                      </div>
                    )}

                    {/* DSNY Schedule Display */}
                    {(recurringScheduleType === 'maintenance_dsny' || newService.dsny_integration) &&
                      (newService.garbage_schedule.length > 0 ||
                        newService.recycling_schedule.length > 0 ||
                        newService.organics_schedule.length > 0 ||
                        newService.bulk_schedule.length > 0) && (
                        <div className="mt-6 space-y-4 border-l-2 border-green-200 pl-4">
                          <h4 className="text-sm font-medium text-gray-700">
                            DSNY Collection Schedules
                          </h4>

                          {newService.garbage_schedule.length > 0 && (
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-gray-600">
                                Garbage Collection
                              </label>
                              <div className="grid grid-cols-7 gap-1.5">
                                {dayNames.map((day) => {
                                  const selected = newService.garbage_schedule.includes(day.key)
                                  return (
                                    <span
                                      key={day.key}
                                      className={`min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                        selected
                                          ? 'bg-red-100 text-red-700 border-red-300'
                                          : 'bg-gray-50 text-gray-400 border-gray-200'
                                      }`}
                                    >
                                      {day.label.slice(0, 3)}
                                    </span>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {newService.recycling_schedule.length > 0 && (
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-gray-600">
                                Recycling Collection
                              </label>
                              <div className="grid grid-cols-7 gap-1.5">
                                {dayNames.map((day) => {
                                  const selected = newService.recycling_schedule.includes(day.key)
                                  return (
                                    <span
                                      key={day.key}
                                      className={`min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                        selected
                                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                                          : 'bg-gray-50 text-gray-400 border-gray-200'
                                      }`}
                                    >
                                      {day.label.slice(0, 3)}
                                    </span>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {newService.organics_schedule.length > 0 && (
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-gray-600">
                                Organics/Compost Collection
                              </label>
                              <div className="grid grid-cols-7 gap-1.5">
                                {dayNames.map((day) => {
                                  const selected = newService.organics_schedule.includes(day.key)
                                  return (
                                    <span
                                      key={day.key}
                                      className={`min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                        selected
                                          ? 'bg-green-100 text-green-700 border-green-300'
                                          : 'bg-gray-50 text-gray-400 border-gray-200'
                                      }`}
                                    >
                                      {day.label.slice(0, 3)}
                                    </span>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {newService.bulk_schedule.length > 0 && (
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-gray-600">
                                Bulk Items Collection
                              </label>
                              <div className="grid grid-cols-7 gap-1.5">
                                {dayNames.map((day) => {
                                  const selected = newService.bulk_schedule.includes(day.key)
                                  return (
                                    <span
                                      key={day.key}
                                      className={`min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                        selected
                                          ? 'bg-purple-100 text-purple-700 border-purple-300'
                                          : 'bg-gray-50 text-gray-400 border-gray-200'
                                      }`}
                                    >
                                      {day.label.slice(0, 3)}
                                    </span>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                )}

                {/* Schedule Configuration - Only for Recurring contracts */}
                {formData.contract_type === 'recurring' &&
                  recurringScheduleType !== 'maintenance_dsny' &&
                  !newService.service_type && (
                    <p className="pt-4 border-t border-gray-200 text-sm text-gray-500">
                      Select a service above to configure its recurrence schedule.
                    </p>
                  )}
                {formData.contract_type === 'recurring' &&
                  (recurringScheduleType === 'maintenance_dsny' || newService.service_type) && (
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Recurrence Schedule Configuration
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Recurrence Type *
                        </label>
                        <select
                          value={newService.recurrence_type}
                          onChange={(e) => {
                            const newType = e.target.value
                            setNewService({
                              ...newService,
                              recurrence_type: newType,
                              recurrence_days: [],
                              recurrence_day_of_month: null,
                              recurrence_weekday: null,
                              recurrence_occurrence: null,
                              recurrence_month: null,
                              recurrence_interval_months: 1,
                              recurrence_interval_weeks: 1,
                              recurrence_interval_days: 1,
                            })
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="weekly">Weekly</option>
                          <option value="bi_weekly">Bi-Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="custom_interval">Custom Interval</option>
                        </select>
                      </div>

                      {/* Weekly & Bi-Weekly: Days of Week */}
                      {(newService.recurrence_type === 'weekly' ||
                        newService.recurrence_type === 'bi_weekly') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Days of Week
                          </label>
                          <div className="grid grid-cols-7 gap-1.5">
                            {dayNames.map((day) => {
                              const selected = newService.recurrence_days.includes(day.key)
                              return (
                                <button
                                  key={day.key}
                                  type="button"
                                  onClick={() => {
                                    const newDays = selected
                                      ? newService.recurrence_days.filter((d) => d !== day.key)
                                      : [...newService.recurrence_days, day.key]
                                    setNewService({ ...newService, recurrence_days: newDays })
                                  }}
                                  className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                    selected
                                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {day.label.slice(0, 3)}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {/* Monthly: Multiple Days of Month OR Nth Weekday */}
                      {newService.recurrence_type === 'monthly' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Schedule Type
                            </label>
                            <select
                              value={newService.recurrence_weekday === null ? 'day_of_month' : 'nth_weekday'}
                              onChange={(e) => {
                                if (e.target.value === 'day_of_month') {
                                  setNewService({
                                    ...newService,
                                    recurrence_days: newService.recurrence_days.length > 0 ? newService.recurrence_days : [1],
                                    recurrence_weekday: null,
                                    recurrence_occurrence: null,
                                  })
                                } else {
                                  setNewService({
                                    ...newService,
                                    recurrence_days: [],
                                    recurrence_weekday: newService.recurrence_weekday ?? 1,
                                    recurrence_occurrence: newService.recurrence_occurrence ?? 1,
                                  })
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="day_of_month">Days of Month (e.g., 1st, 15th, 30th)</option>
                              <option value="nth_weekday">Nth Weekday (e.g., 2nd Monday)</option>
                            </select>
                          </div>

                          {newService.recurrence_weekday === null ? (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Days of Month (1-31)
                              </label>
                              <div className="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                  <div key={day} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      id={`monthly_day_${day}`}
                                      checked={newService.recurrence_days.includes(day)}
                                      onChange={(e) => {
                                        const newDays = e.target.checked
                                          ? [...newService.recurrence_days, day]
                                          : newService.recurrence_days.filter((d) => d !== day)
                                        setNewService({ ...newService, recurrence_days: newDays })
                                      }}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <label
                                      htmlFor={`monthly_day_${day}`}
                                      className="ml-2 text-sm text-gray-700"
                                    >
                                      {day}
                                    </label>
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                Selected: {newService.recurrence_days.length > 0 ? newService.recurrence_days.sort((a, b) => a - b).join(', ') : 'None'}
                              </p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Occurrence
                                </label>
                                <select
                                  value={newService.recurrence_occurrence || 1}
                                  onChange={(e) =>
                                    setNewService({
                                      ...newService,
                                      recurrence_occurrence: parseInt(e.target.value)
                                    })
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="1">1st</option>
                                  <option value="2">2nd</option>
                                  <option value="3">3rd</option>
                                  <option value="4">4th</option>
                                  <option value="5">Last</option>
                                </select>
                              </div>
                              <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Day of Week
                                </label>
                                <select
                                  value={newService.recurrence_weekday ?? 1}
                                  onChange={(e) =>
                                    setNewService({
                                      ...newService,
                                      recurrence_weekday: parseInt(e.target.value)
                                    })
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  {dayNames.map((day) => (
                                    <option key={day.key} value={day.key}>
                                      {day.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Custom Interval: Flexible scheduling */}
                      {newService.recurrence_type === 'custom_interval' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Repeats Every
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                value={
                                  newService.recurrence_interval_days > 1
                                    ? newService.recurrence_interval_days
                                    : newService.recurrence_interval_weeks > 1
                                    ? newService.recurrence_interval_weeks
                                    : newService.recurrence_interval_months || 1
                                }
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1
                                  if (newService.recurrence_interval_days > 1) {
                                    setNewService({ ...newService, recurrence_interval_days: val })
                                  } else if (newService.recurrence_interval_weeks > 1) {
                                    setNewService({ ...newService, recurrence_interval_weeks: val })
                                  } else {
                                    setNewService({ ...newService, recurrence_interval_months: val })
                                  }
                                }}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <select
                                value={
                                  newService.recurrence_interval_days > 1
                                    ? 'days'
                                    : newService.recurrence_interval_weeks > 1
                                    ? 'weeks'
                                    : 'months'
                                }
                                onChange={(e) => {
                                  if (e.target.value === 'days') {
                                    setNewService({
                                      ...newService,
                                      recurrence_interval_days: 2,
                                      recurrence_interval_weeks: 1,
                                      recurrence_interval_months: 1,
                                      recurrence_days: [],
                                      recurrence_weekday: null,
                                      recurrence_occurrence: null,
                                      recurrence_day_of_month: null,
                                      recurrence_month: null,
                                    })
                                  } else if (e.target.value === 'weeks') {
                                    setNewService({
                                      ...newService,
                                      recurrence_interval_weeks: 2,
                                      recurrence_interval_days: 1,
                                      recurrence_interval_months: 1,
                                      recurrence_days: [],
                                      recurrence_weekday: null,
                                      recurrence_occurrence: null,
                                      recurrence_day_of_month: null,
                                      recurrence_month: null,
                                    })
                                  } else {
                                    setNewService({
                                      ...newService,
                                      recurrence_interval_months: 1,
                                      recurrence_interval_weeks: 1,
                                      recurrence_interval_days: 1,
                                    })
                                  }
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="days">Day(s)</option>
                                <option value="weeks">Week(s)</option>
                                <option value="months">Month(s)</option>
                              </select>
                            </div>
                            {/* Quick presets for months */}
                            {newService.recurrence_interval_days <= 1 && newService.recurrence_interval_weeks <= 1 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {[
                                  { label: 'Quarterly', value: 3 },
                                  { label: 'Semi-Annual', value: 6 },
                                  { label: 'Annual', value: 12 },
                                ].map((preset) => (
                                  <button
                                    key={preset.value}
                                    type="button"
                                    onClick={() => setNewService({ ...newService, recurrence_interval_months: preset.value })}
                                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                      newService.recurrence_interval_months === preset.value
                                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                    }`}
                                  >
                                    {preset.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Day selection - For weeks: day-of-week checkboxes, for months: day-of-month selection */}
                          {newService.recurrence_interval_weeks > 1 && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Days of Week
                              </label>
                              <div className="grid grid-cols-7 gap-1.5">
                                {dayNames.map((day) => {
                                  const selected = newService.recurrence_days.includes(day.key)
                                  return (
                                    <button
                                      key={day.key}
                                      type="button"
                                      onClick={() => {
                                        const newDays = selected
                                          ? newService.recurrence_days.filter((d) => d !== day.key)
                                          : [...newService.recurrence_days, day.key]
                                        setNewService({ ...newService, recurrence_days: newDays })
                                      }}
                                      className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                        selected
                                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      {day.label.slice(0, 3)}
                                    </button>
                                  )
                                })}
                              </div>
                              {newService.recurrence_days.length > 0 && (
                                <p className="text-xs text-blue-600 mt-2">
                                  Every {newService.recurrence_interval_weeks} week(s) on: {newService.recurrence_days.sort((a, b) => a - b).map(d => dayNames.find(day => day.key === d)?.label).join(', ')}
                                </p>
                              )}
                            </div>
                          )}

                          {newService.recurrence_interval_days <= 1 && newService.recurrence_interval_weeks <= 1 && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  On Which Day?
                                </label>
                                <select
                                  value={newService.recurrence_weekday === null ? 'day_of_month' : 'nth_weekday'}
                                  onChange={(e) => {
                                    if (e.target.value === 'day_of_month') {
                                      setNewService({
                                        ...newService,
                                        recurrence_days: newService.recurrence_days.length > 0 ? newService.recurrence_days : [1],
                                        recurrence_weekday: null,
                                        recurrence_occurrence: null,
                                      })
                                    } else {
                                      setNewService({
                                        ...newService,
                                        recurrence_days: [],
                                        recurrence_weekday: 1,
                                        recurrence_occurrence: 1,
                                      })
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="day_of_month">Specific Day(s) of the Month</option>
                                  <option value="nth_weekday">A Specific Weekday (e.g., 2nd Monday)</option>
                                </select>
                              </div>

                              {newService.recurrence_weekday === null ? (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Day(s) of Month
                                  </label>
                                  <div className="grid grid-cols-8 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-gray-50">
                                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                      <div key={day} className="flex items-center">
                                        <input
                                          type="checkbox"
                                          id={`custom_day_${day}`}
                                          checked={newService.recurrence_days.includes(day)}
                                          onChange={(e) => {
                                            const newDays = e.target.checked
                                              ? [...newService.recurrence_days, day]
                                              : newService.recurrence_days.filter((d) => d !== day)
                                            setNewService({ ...newService, recurrence_days: newDays })
                                          }}
                                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor={`custom_day_${day}`} className="ml-1 text-sm text-gray-700">
                                          {day}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                  {newService.recurrence_days.length > 0 && (
                                    <p className="text-xs text-blue-600 mt-2">
                                      Every {newService.recurrence_interval_months} month(s) on day(s): {newService.recurrence_days.sort((a, b) => a - b).join(', ')}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-700">On the</span>
                                  <select
                                    value={newService.recurrence_occurrence || 1}
                                    onChange={(e) =>
                                      setNewService({ ...newService, recurrence_occurrence: parseInt(e.target.value) })
                                    }
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="1">1st</option>
                                    <option value="2">2nd</option>
                                    <option value="3">3rd</option>
                                    <option value="4">4th</option>
                                    <option value="5">Last</option>
                                  </select>
                                  <select
                                    value={newService.recurrence_weekday ?? 1}
                                    onChange={(e) =>
                                      setNewService({ ...newService, recurrence_weekday: parseInt(e.target.value) })
                                    }
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    {dayNames.map((day) => (
                                      <option key={day.key} value={day.key}>
                                        {day.label}
                                      </option>
                                    ))}
                                  </select>
                                  <span className="text-sm text-gray-700">of each period</span>
                                </div>
                              )}
                            </>
                          )}

                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Schedule:</strong>{' '}
                              {newService.recurrence_interval_days > 1 ? (
                                `Every ${newService.recurrence_interval_days} day(s)`
                              ) : newService.recurrence_interval_weeks > 1 ? (
                                newService.recurrence_days.length > 0 ? (
                                  `Every ${newService.recurrence_interval_weeks} week(s) on: ${newService.recurrence_days.sort((a, b) => a - b).map(d => dayNames.find(day => day.key === d)?.label).join(', ')}`
                                ) : (
                                  'Please select days of week'
                                )
                              ) : newService.recurrence_weekday !== null ? (
                                `Every ${newService.recurrence_interval_months} month(s) on the ${
                                  ['1st', '2nd', '3rd', '4th', 'Last'][newService.recurrence_occurrence! - 1] || '1st'
                                } ${dayNames.find(d => d.key === newService.recurrence_weekday)?.label || 'Monday'}`
                              ) : newService.recurrence_days.length > 0 ? (
                                `Every ${newService.recurrence_interval_months} month(s) on day(s) ${newService.recurrence_days.sort((a, b) => a - b).join(', ')}`
                              ) : (
                                'Please select days'
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 pt-6 border-t border-gray-200 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddServiceModal(false)
                    setEditingServiceId(null)
                    const defaultRecurrenceType = formData.contract_type === 'one_time' ? 'none' : 'weekly'
                    const isMaintenanceDSNY = recurringScheduleType === 'maintenance_dsny'
                    setNewService({
                      service_type: '',
                      service_name: '',
                      amount: 0,
                      description: '',
                      scheduled_date: '',
                      scheduled_time: '',
                      recurrence_type: defaultRecurrenceType,
                      recurrence_days: [],
                      recurrence_day_of_month: null,
                      recurrence_weekday: null,
                      recurrence_occurrence: null,
                      recurrence_month: null,
                      recurrence_interval_months: 1,
                      recurrence_interval_weeks: 1,
                      recurrence_interval_days: 1,
                      dsny_integration: isMaintenanceDSNY,
                      garbage_schedule: [],
                      recycling_schedule: [],
                      organics_schedule: [],
                      bulk_schedule: [],
                      interior_cleaning_schedule: [],
                    })
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddService}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingServiceId ? 'Update Service' : 'Add Service'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="native-action-bar flex items-center gap-2 sm:gap-3 -mx-4 sm:-mx-6 lg:mx-0 lg:mt-2 lg:rounded-xl lg:border lg:border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className={mobile.primaryBtn}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <SaveIcon className="w-4 h-4" />
                <span className="sm:hidden">Save</span>
                <span className="hidden sm:inline">Save Contract</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/contracts')}
            className={mobile.secondaryBtn}
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Notification Popup Modal */}
      {notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success'
                    ? 'bg-green-100'
                    : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-lg font-semibold ${
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Confirmation Dialog */}
      {linkDialog?.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {linkDialog.type === 'service-to-proposal' && 'Link Service to Proposal Line Item?'}
              {linkDialog.type === 'line-item-to-service' && 'Link Line Item to Service?'}
              {linkDialog.type === 'dsny-to-proposal' && 'Link DSNY Building Upkeep to Proposal?'}
            </h3>

            <div className="mb-4">
              {linkDialog.type === 'service-to-proposal' && linkDialog.matchingProposalItem && (
                <div>
                  <p className="text-sm text-gray-700">
                    A matching proposal line item was found:
                  </p>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {linkDialog.matchingProposalItem.description}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      From proposal line item
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    Would you like to link this service to the proposal line item?
                  </p>
                </div>
              )}

              {linkDialog.type === 'line-item-to-service' && linkDialog.matchingService && (
                <div>
                  <p className="text-sm text-gray-700">
                    A matching contract service was found:
                  </p>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {linkDialog.matchingService.service_name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      From contract services
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    Would you like to link this line item to the contract service?
                  </p>
                </div>
              )}

              {linkDialog.type === 'dsny-to-proposal' && linkDialog.matchingProposalItem && (
                <div>
                  <p className="text-sm text-gray-700">
                    The DSNY Building Upkeep service matches a proposal line item:
                  </p>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {linkDialog.matchingProposalItem.description}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      From proposal line item
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    Would you like to link the DSNY Building Upkeep service to this proposal line item?
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={linkDialog.onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                No, Don't Link
              </button>
              <button
                onClick={linkDialog.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Yes, Link
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
